/**
 * useVoyages.js
 *
 * Domain hook for voyage management - Anchor Mode v2.0 compliant
 * Centralizes all voyage-related storage and business logic
 *
 * Base key: 'operations_voyages' (vessel scoping handled by useStorageState)
 * RBAC-gated mutations
 * No vesselId in records
 * One write per save
 *
 * @example
 * const {
 *   activeVoyage,
 *   startVoyage,
 *   letGo,
 *   allFast,
 *   completeVoyage,
 *   addLogEntry
 * } = useVoyages();
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
import { useAuditTrail } from '../../useAuditTrail';
import {
  VOYAGE_STATUS,
  LOG_ENTRY_TYPES,
  OPERATIONS_STORAGE_KEYS,
  DEFAULT_ARRIVAL_CHECKS,
  OPERATION_CHECKLISTS,
  OPERATION_STATUS,
  CHECK_IMMUTABILITY_WINDOW,
  getOperationTemplate,
  getPhaseOperationChecks
} from '../../../config/operationsConfig';
import { getVoyageStartChecks } from '../../../config/operations/preDepartureChecksLibrary';
import { readJSON } from '../../../lib/storage';
import { getGPSPosition } from '../../../utils/gpsUtils';
import { getOffsetForTimezone, resolveVesselTimezone } from '../../../utils/timezoneUtils';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';
import { azureApiClient } from '../../../lib/azureApiClient';
// Sub-ticket B: snapshot resolution at Let Go. Imported at MODULE SCOPE so it
// is TDZ-safe (FT-270 lesson — never declare hook-touching helpers inside the
// hook body). See voyagePlanResolution.js docblock + CODING-BRIEF-B.md §4.5.
import { resolvePlanForLetGo } from './voyagePlanResolution';
// Sub-ticket C: per-plan-item state mutations. Sibling hook in its own file
// (synthesis A4 TDZ defence — useVoyages.js is 3000+ lines, 2 TDZ regressions
// in FT-270). Composed inside this hook so consumers never touch setVoyages.
// See CODING-BRIEF-C.md §3.4 + §4.5.
import { useVoyagePlanItemState } from './useVoyagePlanItemState';

// RBAC multi-signature shim
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Generate a new voyage number
 * FT-27 Fix: Uses voyageNumberPrefix from voyage settings if configured
 * Format: PREFIX + NNN (sequential) or #NNN if no prefix set
 */
const generateVoyageNumber = (voyages, settings = null) => {
  // Get prefix from settings (e.g., "VY-") or default to "#"
  const prefix = settings?.voyageNumberPrefix || '#';
  const startFrom = settings?.voyageNumberStartFrom || 1;

  if (!voyages || voyages.length === 0) {
    return `${prefix}${String(startFrom).padStart(3, '0')}`;
  }

  // Extract numbers from existing voyage numbers (handles both prefix and # formats)
  const numbers = voyages
    .map(v => {
      // Match any prefix followed by digits, or just digits
      const match = v.voyageNumber?.match(/(?:[^0-9]*)(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(n => !isNaN(n));

  const maxNumber = Math.max(startFrom - 1, ...numbers);
  return `${prefix}${String(maxNumber + 1).padStart(3, '0')}`;
};

/**
 * Generate unique ID for voyage.
 *
 * FT-269: switched from `Date.now()+Math.random()` to `crypto.randomUUID()`
 * so voyage IDs are globally unique across devices (defensible under MAIB
 * scrutiny). crypto.randomUUID is available in all SR-DECK target browsers
 * (iOS Safari 14+, Chrome 92+, Firefox 95+). The fallback path retains the
 * old generator so the function never throws on unexpected environments
 * (Node test runners, atypical embed contexts).
 */
const generateVoyageId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `voyage-${crypto.randomUUID()}`;
  }
  return `voyage-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Generate unique ID for log entry
 */
const generateLogEntryId = () => {
  return `entry-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Generate unique ID for operation
 */
const generateOperationId = () => {
  return `op-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Generate unique ID for pending operation
 */
const generatePendingOperationId = () => {
  return `pending-op-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Normalise voyage data to fix corrupt from/to fields
 * Bug: from.name was sometimes saved as { name: "Hull" } instead of "Hull"
 * This repairs any existing corrupt data on load
 */
/**
 * Pass 2t — Narrow unmatched-section finder for start-time dedup.
 * Distinct from Pass 2o's findActiveSection (which requires planItemId-open
 * OR 60-min ad-hoc check window — and FAILS for a section that's <2s old
 * with zero ticks: lastActivityMs === 0). The bug-#039 forensic shape was
 * exactly that: section written, no checks, no operation_complete.
 *
 * Matching rules:
 *   - planItemId provided  → match by planItemId on section (Pass 2o wiring)
 *   - planItemId absent + operationType==='pre_arrival' → match by operationType
 *     (Pre-Arrival is voyage-singular per design)
 *   - otherwise → no match (FAB ad-hoc ops legitimately re-startable)
 *
 * Returns the matching section's operationId, or null.
 *
 * Module-scope per FT-270 TDZ-defence convention (see header comment lines
 * 46-48). Takes everything as arguments — no closure capture.
 */
const findUnmatchedSectionForStart = (voyage, operationType, planItemId) => {
  const log = voyage?.unifiedLog || [];
  const completedIds = new Set(
    log.filter(e => e && e.type === 'operation_complete' && e.operationId).map(e => e.operationId)
  );
  for (const e of log) {
    if (!e || e.type !== 'section' || !e.operationId) continue;
    if (completedIds.has(e.operationId)) continue;
    if (planItemId) {
      if (e.planItemId === planItemId) return e.operationId;
    } else if (operationType === 'pre_arrival') {
      if (e.operationType === 'pre_arrival') return e.operationId;
    }
  }
  return null;
};

const normaliseVoyage = (voyage) => {
  if (!voyage) return voyage;

  // Helper to extract name string from potentially nested object
  const extractName = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field.name === 'string') return field.name;
    if (typeof field.name?.name === 'string') return field.name.name;
    return '';
  };

  // Normalise from field
  let normalFrom = voyage.from;
  if (voyage.from && typeof voyage.from === 'object') {
    normalFrom = {
      ...voyage.from,
      name: extractName(voyage.from)
    };
  }

  // Normalise to field
  let normalTo = voyage.to;
  if (voyage.to && typeof voyage.to === 'object') {
    normalTo = {
      ...voyage.to,
      name: extractName(voyage.to)
    };
  }

  return {
    ...voyage,
    from: normalFrom,
    to: normalTo
  };
};

export const useVoyages = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // FT-269: belt-and-braces audit chain for every voyage state transition.
  // Retention case `case 'voyages'` is registered in useAuditTrail.js (5y per
  // ISM Code + MGN 478). logChange is referentially stable so it's safe to
  // call from any useCallback below without retriggering deps.
  const { logChange } = useAuditTrail('voyages');
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Base key only - vessel scoping handled by useStorageState
  const { value: rawVoyages = [], setValue: setVoyages } = useStorageState(
    OPERATIONS_STORAGE_KEYS.VOYAGES,
    []
  );

  // Load voyage settings for custom operation templates
  // IMPORTANT: Must use 'voyage_settings' to match VoyageSettingsModal save key
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // Normalise voyages on load to fix any corrupt from/to data
  const voyages = useMemo(() => {
    if (!Array.isArray(rawVoyages)) return [];
    return rawVoyages.map(normaliseVoyage);
  }, [rawVoyages]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get the currently active voyage — PRE_DEPARTURE, UNDERWAY, or ARRIVAL.
   *
   * FT-277 (reverting FT-269): DRAFT is intentionally EXCLUDED from this
   * selector. Consumers that need DRAFT awareness (resume affordance, draft
   * banner) read `draftVoyage` below. Including DRAFT here previously caused:
   *   - VoyageTab blank screen: `renderActiveVoyage()`'s switch has no DRAFT
   *     case, fell through to `default: return null` after back-nav.
   *   - Dashboard React error #31: VesselStatusCard uses
   *     `activeVoyage.from?.name || activeVoyage.from || '---'`. A DRAFT's
   *     empty `from.name = ''` makes the first operand falsy, the OR-chain
   *     falls through to the raw `{name, position}` object, React throws.
   *
   * Keep `draftVoyage` (below) for the resume flow.
   */
  const activeVoyage = useMemo(() => {
    return voyages.find(v =>
      v.status === VOYAGE_STATUS.PRE_DEPARTURE ||
      v.status === VOYAGE_STATUS.UNDERWAY ||
      v.status === VOYAGE_STATUS.ARRIVAL
    ) || null;
  }, [voyages]);

  /**
   * FT-269: Get the current DRAFT voyage for this vessel, if any.
   * Used by StartVoyageFullScreen to decide between "pre-fill from draft"
   * and "create a new draft on mount".
   */
  const draftVoyage = useMemo(() => {
    return voyages.find(v => v.status === VOYAGE_STATUS.DRAFT) || null;
  }, [voyages]);

  /**
   * Get recent completed voyages
   */
  const getRecentVoyages = useCallback((limit = 5) => {
    return voyages
      .filter(v => v.status === VOYAGE_STATUS.COMPLETED)
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
      .slice(0, limit);
  }, [voyages]);

  /**
   * Get voyage by ID
   */
  const getVoyage = useCallback((voyageId) => {
    return voyages.find(v => v.id === voyageId) || null;
  }, [voyages]);

  /**
   * Get active operations for a voyage
   */
  const getActiveOperations = useCallback((voyageId) => {
    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return [];
    return (voyage.operations || []).filter(op => op.status === 'in_progress');
  }, [voyages]);

  // ============================================================================
  // FT-269: DRAFT VOYAGE LIFECYCLE (progressive save)
  // ============================================================================

  /**
   * FT-269: Create a DRAFT voyage. Called by StartVoyageFullScreen on mount
   * so that even back-navigation without any keystroke leaves a persisted
   * draft (Master can resume on any device). Auto-archives to the
   * Archived/Aborted tab if not promoted within 24h.
   *
   * NOTE: this writes through useStorageState — the standard sync path
   * picks it up; nothing bespoke.
   */
  const createDraftVoyage = useCallback(async (initialData = {}) => {
    if (!canDo(can, 'voyages', 'create')) {
      console.warn('[useVoyages] Permission denied: cannot create draft voyage');
      return null;
    }
    const now = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);

    const draft = {
      id: generateVoyageId(),
      voyageNumber: initialData.voyageNumber || generateVoyageNumber(voyages, voyageSettings),
      voyageNumberEditReason: initialData.voyageNumberEditReason || null,
      status: VOYAGE_STATUS.DRAFT,
      from: {
        name: typeof initialData.from === 'string' ? initialData.from : (initialData.from?.name || ''),
        position: initialData.fromPosition || initialData.from?.position || null
      },
      to: {
        name: typeof initialData.to === 'string' ? initialData.to : (initialData.to?.name || ''),
        position: initialData.toPosition || initialData.to?.position || null
      },
      crew: initialData.crew || [],
      crewCount: initialData.crewCount || 0,
      passengerCount: initialData.passengerCount || 0,
      shoreNotified: initialData.shoreNotified || false,
      purpose: initialData.purpose || null,
      eta: initialData.eta || null,
      notes: initialData.notes || '',
      master: initialData.masterDesignation?.masterName || null,
      departureTime: null,
      arrivalTime: null,
      preDepartureChecks: [],
      arrivalChecks: [],
      logEntries: [],
      unifiedLog: [],
      operations: [],
      preferredTimezone: vesselTz, // FT-269: snapshot tz at draft creation
      draftStartedAt: now,
      lastDraftUpdatedAt: now,
      createdAt: now,
      updatedAt: now,
      completedAt: null,

      // FT-278: additive defaults for full-form progressive save
      masterId: initialData.masterId || null,
      masterDesignation: initialData.masterDesignation || null,
      passengerBriefingConfirmed: initialData.passengerBriefingConfirmed || false,

      // FT-278: DRAFT-only transient fields (cleared/re-derived on promotion)
      draftWeather: initialData.draftWeather || { windDirection: '', windSpeed: '', seaState: '', visibility: '' },
      draftFuelData: initialData.draftFuelData || null,
      draftChecks: initialData.draftChecks || {},
      draftTemplateFieldValues: initialData.draftTemplateFieldValues || {},

      // Sub-ticket B: DRAFT-only plan selection.
      // - draftSelectedPlanId points at a row in voyage_settings.voyagePlans.
      // - draftSelectedPlanVersionAtSave is captured at PICK time so the
      //   resume banner can detect drift (admin edited the plan mid-DRAFT).
      // Both are resolved into voyagePlanSnapshot at Let Go and nulled out
      // on promotion to UNDERWAY (see §4.4).
      draftSelectedPlanId: initialData.draftSelectedPlanId || null,
      draftSelectedPlanVersionAtSave:
        typeof initialData.draftSelectedPlanVersionAtSave === 'number'
          ? initialData.draftSelectedPlanVersionAtSave
          : null,

      // FT-278 (Jonathan's §11 decision): FT-107 compliance overrides persist
      // across back-out / resume so the user does not have to re-acknowledge.
      complianceOverrides: initialData.complianceOverrides || []
    };

    await setVoyages([...voyages, draft]);

    // Fire-and-forget audit write — failure is non-fatal.
    logChange({
      action: 'voyage_draft_started',
      item: { id: draft.id, type: 'voyage', name: draft.voyageNumber },
      reason: { code: 'draft_started', label: 'Voyage draft started' },
      moduleSpecificData: {
        eventTime: now,
        eventTimezone: getOffsetForTimezone(vesselTz, new Date(now)),
        recordedAtDevice: now,
        voyageNumber: draft.voyageNumber,
        voyageStatus: VOYAGE_STATUS.DRAFT,
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    return draft;
  }, [can, voyages, voyageSettings, setVoyages, currentVessel, logChange]);

  /**
   * FT-269: Progressive save into an existing DRAFT voyage. Caller is
   * responsible for debouncing (StartVoyageFullScreen debounces at 1s);
   * this hook is intentionally stateless and predictable.
   *
   * Guards: voyage must be DRAFT (cannot progressive-save a committed
   * voyage); permission gate via canDo.
   *
   * NO audit per edit — audit fires only on commit (startVoyage promotion)
   * or explicit abort. Keeps the audit log readable.
   */
  const updateDraftVoyage = useCallback(async (voyageId, updates) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update draft voyage');
      return false;
    }
    if (!voyageId || !updates || typeof updates !== 'object') return false;

    const now = new Date().toISOString();
    let didUpdate = false;

    await setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      if (v.status !== VOYAGE_STATUS.DRAFT) {
        console.warn('[useVoyages] updateDraftVoyage: not a draft, skipping', { voyageId, status: v.status });
        return v;
      }
      didUpdate = true;
      return {
        ...v,
        ...updates,
        // Merge from/to as objects, not overwrite, when caller passes partial shape
        from: updates.from
          ? { ...(v.from || {}), ...(typeof updates.from === 'string' ? { name: updates.from } : updates.from) }
          : v.from,
        to: updates.to
          ? { ...(v.to || {}), ...(typeof updates.to === 'string' ? { name: updates.to } : updates.to) }
          : v.to,
        status: VOYAGE_STATUS.DRAFT, // status is not user-mutable from here
        lastDraftUpdatedAt: now,
        updatedAt: now
      };
    }));

    return didUpdate;
  }, [can, setVoyages]);

  /**
   * FT-269: Abort a DRAFT voyage (DRAFT → ABORTED). Used by the
   * DraftVoyageBanner Discard button AND by the Archived/Aborted tab's
   * "Cancel permanently" button.
   *
   * Auto-archive (24h-stale tab-move) does NOT call this — it's a
   * UI-only filter; the underlying voyage stays in DRAFT status until the
   * user explicitly cancels, so ISM Code retention is preserved.
   */
  const abortDraftVoyage = useCallback(async (voyageId, reason = 'user_discarded') => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot abort draft voyage');
      return false;
    }
    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;
    if (voyage.status !== VOYAGE_STATUS.DRAFT) {
      console.warn('[useVoyages] abortDraftVoyage: not a draft, skipping', { voyageId, status: voyage.status });
      return false;
    }

    const now = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);

    await setVoyages(prev => prev.map(v => v.id === voyageId
      ? {
          ...v,
          status: VOYAGE_STATUS.ABORTED,
          aborted: { reason, abortedAt: now, abortedBy: currentUser?.name || 'Unknown' },
          updatedAt: now
        }
      : v
    ));

    logChange({
      action: 'voyage_aborted',
      item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
      reason: { code: reason, label: 'Voyage draft aborted' },
      moduleSpecificData: {
        eventTime: now,
        eventTimezone: getOffsetForTimezone(vesselTz, new Date(now)),
        recordedAtDevice: now,
        voyageNumber: voyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.ABORTED,
        priorStatus: VOYAGE_STATUS.DRAFT,
        abortReason: reason,
        draftStartedAt: voyage.draftStartedAt || null,
        lastDraftUpdatedAt: voyage.lastDraftUpdatedAt || null,
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    return true;
  }, [can, voyages, setVoyages, currentVessel, currentUser, logChange]);

  // ============================================================================
  // VOYAGE LIFECYCLE ACTIONS
  // ============================================================================

  /**
   * Start a new voyage
   * Creates voyage in pre_departure status
   */
  const startVoyage = useCallback(async (voyageData) => {
    if (!canDo(can, 'voyages', 'create')) {
      console.warn('[useVoyages] Permission denied: cannot create voyage');
      return null;
    }

    // SAFETY CHECK: Prevent overlapping voyages
    const existingActiveVoyage = voyages.find(v =>
      v.status === VOYAGE_STATUS.PRE_DEPARTURE ||
      v.status === VOYAGE_STATUS.UNDERWAY ||
      v.status === VOYAGE_STATUS.ARRIVAL
    );

    if (existingActiveVoyage) {
      console.warn('[useVoyages] Cannot start voyage: another voyage is already active', {
        attemptedAction: 'startVoyage',
        existingVoyageId: existingActiveVoyage.id,
        existingVoyageStatus: existingActiveVoyage.status
      });
      return {
        error: 'VOYAGE_ALREADY_ACTIVE',
        activeVoyageId: existingActiveVoyage.id,
        activeVoyageStatus: existingActiveVoyage.status,
        message: 'A voyage is already in progress. Complete or cancel that voyage first.'
      };
    }

    // FT-111 FIX: Read user's customised pre-departure checklist from settings
    // The key is checklist_configs_{vesselId} (org-scoping handled by readJSON/toOrgScopedKey)
    // Pre-departure items are at .pre_departure.items
    const vesselId = currentVessel?.id || 'default';
    const checklistConfigs = readJSON(`checklist_configs_${vesselId}`, null);
    const preDepartureItems = checklistConfigs?.pre_departure?.items;

    let preDepartureChecks = [];

    if (preDepartureItems && preDepartureItems.length > 0) {
      // FT-111: Use user's customised checklist from Settings
      // Map each item to voyage check format (all items required per Phase 9 decision)
      preDepartureChecks = preDepartureItems.map(item => ({
        id: item.id,
        text: item.text,
        category: item.category,
        completed: false,
        completedAt: null,
        completedBy: null
      }));
    } else {
      // Fallback: No custom config, use hardcoded library defaults
      const voyageChecks = getVoyageStartChecks(voyageData.passengerCount > 0);

      // Add required checks
      voyageChecks.required.forEach(check => {
        preDepartureChecks.push({
          ...check,
          completed: false,
          completedAt: null,
          completedBy: null
        });
      });

      // Add optional checks
      voyageChecks.optional.forEach(check => {
        preDepartureChecks.push({
          ...check,
          completed: false,
          completedAt: null,
          completedBy: null
        });
      });

      // Add conditional checks (e.g., passenger briefing if passengers > 0)
      if (voyageData.passengerCount > 0 && voyageChecks.conditional?.passengers) {
        voyageChecks.conditional.passengers.forEach(check => {
          preDepartureChecks.push({
            ...check,
            completed: false,
            completedAt: null,
            completedBy: null
          });
        });
      }
    }

    // Build arrival checks - use phase operations if available, fallback to defaults
    const phaseChecks = getPhaseOperationChecks('post_arrival', voyageSettings);
    const arrivalCheckSource = phaseChecks.length > 0 ? phaseChecks : DEFAULT_ARRIVAL_CHECKS;
    const arrivalChecks = arrivalCheckSource.map(check => ({
      ...check,
      completed: false,
      completedAt: null,
      completedBy: null
    }));

    // FT-269: if a DRAFT exists for this vessel, promote it instead of
    // creating a new record. Preserves the draft's id (so audit chain
    // history remains threaded) and the draftStartedAt timestamp for
    // forensics.
    const nowIso = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);
    const eventTzOffset = getOffsetForTimezone(vesselTz, new Date(nowIso));
    const existingDraft = voyages.find(v => v.status === VOYAGE_STATUS.DRAFT);

    const baseRecord = existingDraft
      ? { ...existingDraft }
      : {
          id: generateVoyageId(),
          createdAt: nowIso,
          draftStartedAt: null,
          lastDraftUpdatedAt: null
        };

    const newVoyage = {
      ...baseRecord,
      voyageNumber: voyageData.voyageNumber || baseRecord.voyageNumber || generateVoyageNumber(voyages, voyageSettings),
      voyageNumberEditReason: voyageData.voyageNumberEditReason || baseRecord.voyageNumberEditReason || null,
      status: VOYAGE_STATUS.PRE_DEPARTURE,
      from: {
        name: typeof voyageData.from === 'string' ? voyageData.from : (voyageData.from?.name || ''),
        position: voyageData.fromPosition || voyageData.from?.position || null
      },
      to: {
        name: typeof voyageData.to === 'string' ? voyageData.to : (voyageData.to?.name || ''),
        position: voyageData.toPosition || voyageData.to?.position || null
      },
      crew: voyageData.crew || baseRecord.crew || [],
      crewCount: voyageData.crewCount || baseRecord.crewCount || 0,
      passengerCount: voyageData.passengerCount || baseRecord.passengerCount || 0,
      passengerBriefRequired: (voyageData.passengerCount || 0) > 0,
      shoreNotified: voyageData.shoreNotified || baseRecord.shoreNotified || false,
      purpose: voyageData.purpose || baseRecord.purpose || null,
      eta: voyageData.eta || baseRecord.eta || null,
      notes: voyageData.notes || baseRecord.notes || '',
      master: voyageData.masterDesignation?.masterName || baseRecord.master || null,  // FT-217: Simple master name for display
      preferredTimezone: vesselTz, // FT-269: persist tz at promotion for downstream display
      departureTime: null,
      arrivalTime: null,
      preDepartureChecks,
      arrivalChecks,
      logEntries: baseRecord.logEntries || [],
      unifiedLog: baseRecord.unifiedLog || [],
      operations: baseRecord.operations || [],
      updatedAt: nowIso,
      completedAt: null
    };

    // FT-269: append or replace depending on whether we promoted a DRAFT.
    const updated = existingDraft
      ? voyages.map(v => v.id === existingDraft.id ? newVoyage : v)
      : [...voyages, newVoyage];
    await setVoyages(updated);

    // FT-269: belt-and-braces audit chain entry. Legacy logCRUDOperation
    // call is preserved below (separate storage path); both fire so existing
    // downstream consumers keep working.
    logChange({
      action: 'voyage_started',
      item: { id: newVoyage.id, type: 'voyage', name: newVoyage.voyageNumber },
      reason: { code: existingDraft ? 'draft_promoted' : 'voyage_started', label: existingDraft ? 'Draft promoted to voyage' : 'Voyage started' },
      moduleSpecificData: {
        eventTime: nowIso,
        eventTimezone: eventTzOffset,
        recordedAtDevice: nowIso,
        voyageNumber: newVoyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.PRE_DEPARTURE,
        promotedFromDraft: !!existingDraft,
        draftStartedAt: existingDraft?.draftStartedAt || null,
        from: newVoyage.from?.name || null,
        to: newVoyage.to?.name || null,
        crewCount: newVoyage.crewCount,
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    // Legacy audit trail (separate storage path) — left untouched per brief §17.
    try {
      await logCRUDOperation(
        AUDIT_ACTIONS.CREATE,
        'voyages',
        { id: newVoyage.id, vesselId: currentVessel?.id, voyageNumber: newVoyage.voyageNumber },
        { userId: currentUser?.id, name: currentUser?.name || 'Unknown' }
      );
    } catch (auditError) {
      console.warn('[useVoyages] Audit trail logging failed:', auditError);
    }

    return newVoyage;
  }, [can, voyages, voyageSettings, setVoyages, currentVessel, currentUser, logChange]);

  /**
   * Start voyage AND immediately depart (skip PRE_DEPARTURE phase)
   * Used by the new unified Start Voyage screen
   *
   * @param {Object} voyageData - Voyage details (from, to, crew, etc.)
   * @param {Object} completedChecks - Pre-departure checks already completed inline { checkId: boolean }
   * @param {Object} weatherData - Initial weather observation (optional)
   * @param {Object} fuelData - Departure fuel level { percentage, value }
   * @param {Object} checkConfig - Optional custom check config from usePreDepartureConfig
   * @param {Object} options - FT-280: { eventTime, eventTimezone, delayReason } | null — the Master's picked Let Go time.
   */
  const startVoyageAndDepart = useCallback(async (voyageData, completedChecks, weatherData, fuelData, checkConfig = null, options = null) => {
    if (!canDo(can, 'voyages', 'create')) {
      console.warn('[useVoyages] Permission denied: cannot create voyage');
      return null;
    }

    // SAFETY CHECK: Prevent overlapping voyages
    const existingActiveVoyage = voyages.find(v =>
      v.status === VOYAGE_STATUS.PRE_DEPARTURE ||
      v.status === VOYAGE_STATUS.UNDERWAY ||
      v.status === VOYAGE_STATUS.ARRIVAL
    );

    if (existingActiveVoyage) {
      console.warn('[useVoyages] Cannot start voyage: another voyage is already active', {
        attemptedAction: 'startVoyageAndDepart',
        existingVoyageId: existingActiveVoyage.id,
        existingVoyageStatus: existingActiveVoyage.status
      });
      return {
        error: 'VOYAGE_ALREADY_ACTIVE',
        activeVoyageId: existingActiveVoyage.id,
        activeVoyageStatus: existingActiveVoyage.status,
        message: 'A voyage is already in progress. Complete or cancel that voyage first.'
      };
    }

    // FT-280: parse options + resolve event time/timezone vs record time.
    // Mirrors the `allFast` pattern (useVoyages.js: allFast definition below).
    const recordedAtDevice = new Date().toISOString();
    const vesselTzForLetGo = resolveVesselTimezone(currentVessel);
    let eventTime = recordedAtDevice;
    let eventTimezone = getOffsetForTimezone(vesselTzForLetGo, new Date(recordedAtDevice));
    let delayReason;
    if (options && typeof options === 'object') {
      if (options.eventTime) eventTime = options.eventTime;
      if (options.eventTimezone) eventTimezone = options.eventTimezone;
      if (options.delayReason) delayReason = options.delayReason;
    }
    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const auditContext = networkStatusAtRecord ? { networkStatusAtRecord } : undefined;
    const deltaSeconds = Math.round((Date.now() - new Date(eventTime).getTime()) / 1000);

    // `now` keeps backwards-compat in the function body — it now means "the canonical
    // voyage event time" not "click time". Downstream uses (departureTime, milestone
    // timestamps, weather entry) all want the canonical event time.
    const now = eventTime;

    // FT-279: detect an existing DRAFT to promote in place rather than
    // appending a fresh record alongside it. Preserves id + voyageNumber
    // + draftStartedAt so audit chain history stays threaded and the user
    // does not see two voyage numbers / a lingering banner after Let Go.
    // Mirrors the pattern proven by startVoyage (lines 554-610).
    if (voyages.filter(v => v.status === VOYAGE_STATUS.DRAFT).length > 1) {
      console.warn('[useVoyages] startVoyageAndDepart: multiple DRAFTs detected for vessel — promoting first match, orphans will remain', {
        draftCount: voyages.filter(v => v.status === VOYAGE_STATUS.DRAFT).length,
        draftIds: voyages.filter(v => v.status === VOYAGE_STATUS.DRAFT).map(v => v.id)
      });
    }
    const existingDraft = voyages.find(v => v.status === VOYAGE_STATUS.DRAFT);

    // Sub-ticket B: resolve the DRAFT's selected plan against the CURRENT
    // vessel library at Let Go. Pure + side-effect-free — see
    // voyagePlanResolution.js.
    //
    // Snapshot-read-site rule (synthesis P4.6): this is the ONLY place that
    // reads voyage_settings.voyagePlans for an UNDERWAY-bound voyage. The
    // snapshot we write onto the voyage record is the canonical source
    // from now on; Sub-tickets C+D never read the library again.
    const libraryPlansAtLetGo = Array.isArray(voyageSettings?.voyagePlans)
      ? voyageSettings.voyagePlans
      : [];
    const operationsListAtLetGo = Array.isArray(voyageSettings?.operationsList)
      ? voyageSettings.operationsList
      : [];
    const {
      resolution: voyagePlanResolution,
      snapshot: voyagePlanSnapshot,
      items: voyagePlanItems,
    } = resolvePlanForLetGo(
      existingDraft?.draftSelectedPlanId || null,
      typeof existingDraft?.draftSelectedPlanVersionAtSave === 'number'
        ? existingDraft.draftSelectedPlanVersionAtSave
        : null,
      libraryPlansAtLetGo,
      operationsListAtLetGo,
      currentVessel?.id || null
    );

    const baseRecord = existingDraft
      ? { ...existingDraft }
      : {
          id: generateVoyageId(),
          createdAt: now,
          draftStartedAt: null,
          lastDraftUpdatedAt: null
        };

    // Build pre-departure checks - use custom config if provided, else defaults from library
    const preDepartureChecks = [];
    const voyageChecks = getVoyageStartChecks(voyageData.passengerCount > 0);

    if (checkConfig?.checks) {
      // Use custom check configuration (from usePreDepartureConfig)
      checkConfig.checks.forEach(check => {
        const isCompleted = check.type === 'fuel'
          ? (fuelData !== null)
          : (completedChecks[check.id] || false);

        preDepartureChecks.push({
          ...check,
          completed: isCompleted,
          completedAt: isCompleted ? now : null,
          completedBy: isCompleted ? currentUser?.name || 'User' : null,
          fuelData: check.type === 'fuel' ? fuelData : undefined,
          source: check.source || 'custom'
        });
      });

      // Still add conditional checks if applicable
      if (voyageData.passengerCount > 0 && voyageChecks.conditional?.passengers) {
        voyageChecks.conditional.passengers.forEach(check => {
          const isCompleted = completedChecks[check.id] || false;
          preDepartureChecks.push({
            ...check,
            completed: isCompleted,
            completedAt: isCompleted ? now : null,
            completedBy: isCompleted ? currentUser?.name || 'User' : null,
            source: 'conditional'
          });
        });
      }
    } else {
      // Use checks from library
      // Add required checks
      voyageChecks.required.forEach(check => {
        const isCompleted = check.type === 'fuel' ? (fuelData !== null) : (completedChecks[check.id] || false);
        preDepartureChecks.push({
          ...check,
          completed: isCompleted,
          completedAt: isCompleted ? now : null,
          completedBy: isCompleted ? currentUser?.name || 'User' : null,
          fuelData: check.type === 'fuel' ? fuelData : undefined,
          source: 'library'
        });
      });

      // Add optional checks
      voyageChecks.optional.forEach(check => {
        const isCompleted = completedChecks[check.id] || false;
        preDepartureChecks.push({
          ...check,
          completed: isCompleted,
          completedAt: isCompleted ? now : null,
          completedBy: isCompleted ? currentUser?.name || 'User' : null,
          source: 'library'
        });
      });

      // Add conditional checks (e.g., passenger briefing if passengers > 0)
      if (voyageData.passengerCount > 0 && voyageChecks.conditional?.passengers) {
        voyageChecks.conditional.passengers.forEach(check => {
          const isCompleted = completedChecks[check.id] || false;
          preDepartureChecks.push({
            ...check,
            completed: isCompleted,
            completedAt: isCompleted ? now : null,
            completedBy: isCompleted ? currentUser?.name || 'User' : null,
            source: 'conditional'
          });
        });
      }
    }

    // Build arrival checks - use phase operations if available, fallback to defaults
    const phaseChecks = getPhaseOperationChecks('post_arrival', voyageSettings);
    const arrivalCheckSource = phaseChecks.length > 0 ? phaseChecks : DEFAULT_ARRIVAL_CHECKS;
    const arrivalChecks = arrivalCheckSource.map(check => ({
      ...check,
      completed: false,
      completedAt: null,
      completedBy: null
    }));

    // Build initial unified log with completed checks and LET GO
    const unifiedLog = [];

    // Add completed pre-departure checks to unified log
    preDepartureChecks.filter(c => c.completed).forEach(check => {
      unifiedLog.push({
        id: `check-${check.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'check',
        checkType: 'pre_departure',
        checkId: check.id,
        text: check.text,
        completed: true,  // FIXED: was 'status: completed' - LogEntry checks for 'completed: true'
        completedAt: now,
        timestamp: now,
        completedBy: currentUser?.name || 'User'
      });
    });

    // Add pre-departure custom fields to unified log (same pattern as mid-voyage operations)
    // FT-202: Custom data fields from Voyage Settings should appear in voyage log
    if (voyageData.preDepartureFields && Object.keys(voyageData.preDepartureFields).length > 0) {
      unifiedLog.push({
        id: `pre-departure-data-${Date.now()}`,
        type: 'operation_data',
        operationType: 'pre_departure',
        locked: true,
        timestamp: now,
        createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
        ...voyageData.preDepartureFields
      });
    }

    // Add weather entry if provided
    if (weatherData && (weatherData.windDirection || weatherData.windSpeed || weatherData.seaState || weatherData.visibility)) {
      unifiedLog.push({
        id: `weather-departure-${Date.now()}`,
        type: 'weather',
        timestamp: now,
        data: weatherData,
        recordedBy: currentUser?.name || 'User'
      });
    }

    // Add LET GO milestone — FT-280 carries event-time fields so the MCA
    // record reflects the Master's claimed departure time, not click time.
    unifiedLog.push({
      id: `letgo-${Date.now()}`,
      type: 'milestone',
      milestoneType: 'let_go',
      timestamp: now,                              // event time (== eventTime)
      eventTime,                                   // FT-280
      eventTimezone,                               // FT-280
      recordedAtDevice,                            // FT-280
      recordedAtServer: null,                      // FT-271's job
      ...(delayReason ? { delayReason } : {}),     // FT-280
      ...(auditContext ? { auditContext } : {}),   // FT-280
      data: {
        from: voyageData.from,
        crewCount: voyageData.crewCount,
        passengerCount: voyageData.passengerCount,
        departureFuel: fuelData?.percentage || fuelData?.value || null
      },
      recordedBy: currentUser?.name || 'User'
    });

    const newVoyage = {
      ...baseRecord,
      voyageNumber: voyageData.voyageNumber
        || baseRecord.voyageNumber
        || generateVoyageNumber(voyages, voyageSettings),
      voyageNumberEditReason: voyageData.voyageNumberEditReason
        || baseRecord.voyageNumberEditReason
        || null,
      status: VOYAGE_STATUS.UNDERWAY,  // Skip PRE_DEPARTURE - go straight to UNDERWAY
      from: {
        name: typeof voyageData.from === 'string' ? voyageData.from : (voyageData.from?.name || ''),
        position: voyageData.fromPosition || voyageData.from?.position || null
      },
      to: {
        name: typeof voyageData.to === 'string' ? voyageData.to : (voyageData.to?.name || ''),
        position: voyageData.toPosition || voyageData.to?.position || null
      },
      crew: voyageData.crew || baseRecord.crew || [],
      crewCount: voyageData.crewCount || baseRecord.crewCount || 0,
      passengerCount: voyageData.passengerCount || baseRecord.passengerCount || 0,
      passengerBriefRequired: (voyageData.passengerCount || 0) > 0,
      passengerBriefingConfirmed: voyageData.passengerBriefingConfirmed
        || baseRecord.passengerBriefingConfirmed || false,  // FT-149
      shoreNotified: voyageData.shoreNotified || baseRecord.shoreNotified || false,
      purpose: voyageData.purpose || baseRecord.purpose || null,
      eta: voyageData.eta || baseRecord.eta || null,
      notes: voyageData.notes || baseRecord.notes || '',
      preDepartureNotes: voyageData.preDepartureNotes || baseRecord.preDepartureNotes || '',  // FT-150
      preDepartureFields: voyageData.preDepartureFields || baseRecord.preDepartureFields || null,  // FT-202: Custom data fields from Voyage Settings
      complianceOverrides: voyageData.complianceOverrides
        || baseRecord.complianceOverrides || [],  // FT-107: Compliance override records
      master: voyageData.masterDesignation?.masterName || baseRecord.master || null,  // FT-217: Simple master name for display
      masterId: voyageData.masterId || baseRecord.masterId || null,
      masterDesignation: voyageData.masterDesignation || baseRecord.masterDesignation || null,
      preferredTimezone: resolveVesselTimezone(currentVessel),  // FT-269
      departureTime: now,  // Set departure time immediately
      departureFuel: fuelData?.percentage || fuelData?.value || null,
      departureWeather: weatherData || null,
      arrivalTime: null,
      preDepartureChecks,
      arrivalChecks,
      logEntries: baseRecord.logEntries || [],
      unifiedLog: [...(baseRecord.unifiedLog || []), ...unifiedLog],
      operations: baseRecord.operations || [],

      // FT-279: DRAFT-only transient fields explicitly cleared on promotion.
      // Canonical replacements come from the Let Go form:
      //   draftWeather       → departureWeather (above)
      //   draftFuelData      → departureFuel (above)
      //   draftChecks        → preDepartureChecks (above)
      //   draftTemplateFieldValues → preDepartureFields (above)
      // draftStartedAt is PRESERVED via the baseRecord spread for audit forensics.
      // Sub-ticket B: draftSelectedPlanId / draftSelectedPlanVersionAtSave are
      // DRAFT-only — their resolved-at-Let-Go form lives in voyagePlanSnapshot
      // / voyagePlanResolution (added below).
      draftWeather: null,
      draftFuelData: null,
      draftChecks: null,
      draftTemplateFieldValues: null,
      draftSelectedPlanId: null,
      draftSelectedPlanVersionAtSave: null,
      lastDraftUpdatedAt: null,

      updatedAt: now,
      completedAt: null,

      // Sub-ticket B: plan snapshot fields. Always-present so consumers can
      // destructure without a defensive nullish check on the parent object.
      // Backward compat: voyages predating B carry NONE of these — every
      // read site MUST tolerate `undefined` (see voyagePlanResolution.js
      // docblock + CODING-BRIEF-B.md §6.1).
      voyagePlanSnapshot,
      voyagePlanResolution,
      voyagePlanItems
    };

    // FT-279: replace the DRAFT in place if it existed, else append (cold start).
    // Same-id replacement keeps voyageNumber stable and the banner disappears
    // because the DRAFT record is gone from the array.
    const updated = existingDraft
      ? voyages.map(v => v.id === existingDraft.id ? newVoyage : v)
      : [...voyages, newVoyage];
    await setVoyages(updated);

    // FT-269 §9.3 / FT-280 §4.3: belt-and-braces audit chain entry for state
    // transition. startVoyageAndDepart skips PRE_DEPARTURE and goes straight
    // to UNDERWAY — treated as one `voyage_started` action.
    // FT-280 enrichments: eventTime/eventTimezone come from picked time (not
    // recompute), recordedAtDevice is the actual click time, delayReason +
    // deltaSeconds + networkStatusAtRecord captured when applicable.
    logChange({
      action: 'voyage_started',
      item: { id: newVoyage.id, type: 'voyage', name: newVoyage.voyageNumber },
      reason: delayReason?.code
        ? { code: delayReason.code, label: 'Backdated Let Go', notes: delayReason.note || undefined }
        : (existingDraft
            ? { code: 'draft_promoted_letgo', label: 'Draft promoted directly to UNDERWAY' }
            : { code: 'voyage_started', label: 'Voyage started and departed' }),
      moduleSpecificData: {
        eventTime,
        eventTimezone,
        recordedAtDevice,
        voyageNumber: newVoyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.UNDERWAY,
        promotedFromDraft: !!existingDraft,
        draftStartedAt: existingDraft?.draftStartedAt || null,
        from: newVoyage.from?.name || null,
        to: newVoyage.to?.name || null,
        crewCount: newVoyage.crewCount,
        passengerCount: newVoyage.passengerCount,
        ...(networkStatusAtRecord ? { networkStatusAtRecord } : {}),
        ...(delayReason ? { delayReason } : {}),
        ...(deltaSeconds > 30 ? { deltaSeconds } : {}),
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] startVoyageAndDepart audit failed (non-fatal):', err));

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.CREATE,
        'voyages',
        { id: newVoyage.id, vesselId: currentVessel?.id, voyageNumber: newVoyage.voyageNumber, action: 'start_and_depart' },
        { userId: currentUser?.id, name: currentUser?.name || 'Unknown' }
      );
    } catch (auditError) {
      console.warn('[useVoyages] Audit trail logging failed:', auditError);
    }

    console.log('[useVoyages] Created voyage and departed:', newVoyage.voyageNumber);
    return newVoyage;
    // Sub-ticket B: voyageSettings added — resolvePlanForLetGo reads
    // voyageSettings.voyagePlans + voyageSettings.operationsList.
  }, [can, voyages, setVoyages, currentUser, currentVessel, logChange, voyageSettings]);

  /**
   * Update pre-departure check item
   * @param {string} voyageId - Voyage ID
   * @param {string} checkId - Check ID
   * @param {boolean} completed - Completion status
   * @param {object} extraData - Optional extra data (e.g., { fuelData } for fuel checks)
   */
  const updatePreDepartureCheck = useCallback(async (voyageId, checkId, completed, extraData = {}) => {
    console.log('[useVoyages] updatePreDepartureCheck called:', { voyageId, checkId, completed, extraData });

    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    // Use functional update to always get latest voyages state
    let success = false;

    await setVoyages(prevVoyages => {
      const voyage = prevVoyages.find(v => v.id === voyageId);
      if (!voyage || voyage.status !== VOYAGE_STATUS.PRE_DEPARTURE) {
        console.warn('[useVoyages] Voyage not found or wrong status:', { voyageId, status: voyage?.status });
        return prevVoyages; // No change
      }

      const updatedChecks = voyage.preDepartureChecks.map(check =>
        check.id === checkId
          ? {
              ...check,
              completed,
              completedAt: completed ? new Date().toISOString() : null,
              completedBy: completed ? currentUser?.name || 'Unknown' : null,
              ...extraData  // Spread extra data (e.g., fuelData)
            }
          : check
      );

      success = true;
      console.log('[useVoyages] Updated check:', updatedChecks.find(c => c.id === checkId));

      return prevVoyages.map(v =>
        v.id === voyageId
          ? {
              ...v,
              preDepartureChecks: updatedChecks,
              updatedAt: new Date().toISOString()
            }
          : v
      );
    });

    return success;
  }, [can, setVoyages, currentUser]);

  /**
   * Check if all required pre-departure checks are complete
   * For fuel-type checks, also accepts fuelData as completion indicator
   */
  const canLetGo = useCallback((voyageId) => {
    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const requiredChecks = voyage.preDepartureChecks.filter(c => c.required);
    return requiredChecks.every(c => {
      // For fuel checks, check if fuelData is set
      if (c.type === 'fuel') {
        return c.completed || c.fuelData;
      }
      return c.completed;
    });
  }, [voyages]);

  /**
   * Let Go - transition from pre_departure to underway.
   *
   * FT-269: signature now accepts a structured options object so the
   * Master can record an actual event time distinct from the click time.
   *
   * Backwards-compat: a bare ISO string is interpreted as `eventTime`
   * with current-vessel-tz + no delayReason — keeps any legacy caller
   * working without modification.
   *
   * @param {string} voyageId
   * @param {Object|string|null} [options] - { eventTime, eventTimezone, delayReason } or legacy ISO string
   */
  const letGo = useCallback(async (voyageId, options = null) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage || voyage.status !== VOYAGE_STATUS.PRE_DEPARTURE) {
      return false;
    }

    // SAFETY CHECK: Block if another voyage is already UNDERWAY or ARRIVAL
    const otherActiveVoyage = voyages.find(v =>
      v.id !== voyageId &&
      (v.status === VOYAGE_STATUS.UNDERWAY || v.status === VOYAGE_STATUS.ARRIVAL)
    );
    if (otherActiveVoyage) {
      console.warn('[useVoyages] Cannot Let Go: another voyage is already active', otherActiveVoyage.id);
      return { error: 'VOYAGE_ALREADY_ACTIVE', activeVoyageId: otherActiveVoyage.id };
    }

    // Check if all required checks are complete
    if (!canLetGo(voyageId)) {
      console.warn('[useVoyages] Cannot Let Go: required checks incomplete');
      return false;
    }

    // FT-269: resolve event time vs record time. The Master may have backdated
    // via TimePickerModal — eventTime is what actually happened, recordedAtDevice
    // is when they clicked. Both are stored; display uses eventTime.
    const recordedAtDevice = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);
    let eventTime = recordedAtDevice;
    let eventTimezone = getOffsetForTimezone(vesselTz, new Date(recordedAtDevice));
    let delayReason;
    if (typeof options === 'string') {
      eventTime = options;
      eventTimezone = getOffsetForTimezone(vesselTz, new Date(eventTime));
    } else if (options && typeof options === 'object') {
      if (options.eventTime) eventTime = options.eventTime;
      if (options.eventTimezone) eventTimezone = options.eventTimezone;
      if (options.delayReason) delayReason = options.delayReason;
    }

    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const auditContext = (networkStatusAtRecord || delayReason)
      ? Object.fromEntries(Object.entries({
          networkStatusAtRecord,
          // outboxAttempts / connectivityRegainedAt are populated later by
          // the server-stamp listener path, see useEffect at the bottom of
          // this hook.
        }).filter(([, v]) => v !== null && v !== undefined))
      : undefined;

    const deltaSeconds = Math.round((Date.now() - new Date(eventTime).getTime()) / 1000);
    const now = eventTime;

    // === V2: Build unified log entries ===
    const unifiedLogEntries = [];

    // 1. Add section header for pre-departure
    unifiedLogEntries.push({
      id: `pd_section_${Date.now()}`,
      type: 'section',
      operationType: 'pre_departure',
      timestamp: voyage.createdAt || now,
      phase: 'pre_departure'
    });

    // 2. Add each completed pre-departure check to unified log
    const sortedChecks = [...(voyage.preDepartureChecks || [])]
      .filter(c => c.completed)
      .sort((a, b) => {
        const timeA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const timeB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return timeA - timeB;
      });

    sortedChecks.forEach(check => {
      unifiedLogEntries.push({
        id: `pd_check_${check.id}_${Date.now()}`,
        type: 'check',
        text: check.text,
        completed: true,
        timestamp: check.completedAt || now,
        phase: 'pre_departure',
        required: check.required || false
      });
    });

    // 3. Add LET GO milestone — FT-269: carries the regulatory-grade
    // event-time/record-time pair and optional delayReason/auditContext.
    const letGoMilestone = {
      id: `milestone_let_go_${Date.now()}`,
      type: 'milestone',
      milestoneType: 'let_go',
      location: voyage.from?.name || null,
      timestamp: now, // legacy field == eventTime (display layer prefers eventTime)
      eventTime,
      eventTimezone,
      recordedAtDevice,
      recordedAtServer: null, // populated by sr:voyage:server-stamp listener / reconciler
      ...(delayReason ? { delayReason } : {}),
      ...(auditContext ? { auditContext } : {}),
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };
    unifiedLogEntries.push(letGoMilestone);

    // === Legacy: Keep logEntries for backward compatibility ===
    const letGoEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.LET_GO,
      timestamp: now,
      eventTime,
      eventTimezone,
      recordedAtDevice,
      recordedAtServer: null,
      ...(delayReason ? { delayReason } : {}),
      ...(auditContext ? { auditContext } : {}),
      data: {
        location: voyage.from?.name,
        position: voyage.from?.position
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const preDepartureEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.PRE_DEPARTURE_COMPLETE,
      timestamp: now,
      data: {
        requiredChecks: voyage.preDepartureChecks?.filter(c => c.required).length || 0,
        optionalChecks: voyage.preDepartureChecks?.filter(c => !c.required && c.completed).length || 0
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    // Update voyage. FT-269: departureTime stores eventTime (NOT click time)
    // so downstream consumers (PDF, history view) see actual cast-off.
    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            status: VOYAGE_STATUS.UNDERWAY,
            departureTime: eventTime,
            logEntries: [...v.logEntries, preDepartureEntry, letGoEntry],
            unifiedLog: [...(v.unifiedLog || []), ...unifiedLogEntries],
            updatedAt: new Date().toISOString()
          }
        : v
    );

    await setVoyages(updated);

    // FT-269: belt-and-braces audit chain entry.
    logChange({
      action: 'voyage_let_go',
      item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
      reason: delayReason?.code
        ? { code: delayReason.code, label: 'Backdated Let Go', notes: delayReason.note || undefined }
        : { code: 'let_go', label: 'Voyage Let Go' },
      moduleSpecificData: {
        eventTime,
        eventTimezone,
        recordedAtDevice,
        eventId: letGoMilestone.id,
        voyageNumber: voyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.UNDERWAY,
        from: voyage.from?.name || null,
        ...(networkStatusAtRecord ? { networkStatusAtRecord } : {}),
        ...(delayReason ? { delayReason } : {}),
        ...(deltaSeconds > 30 ? { deltaSeconds } : {}),
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    return true;
  }, [can, voyages, setVoyages, currentVessel, currentUser, canLetGo, logChange]);

  /**
   * All Fast - transition from underway to arrival.
   *
   * FT-269: signature extended. Accepts (voyageId, options) where options is
   * `{ eventTime, eventTimezone, delayReason, arrivalLocation }`.
   * Backwards-compat: an ISO string in position 2 is treated as legacy
   * arrivalTime (matches prior signature).
   *
   * @param {string} voyageId
   * @param {Object|string|null} [options]
   * @param {string|null} [legacyArrivalLocation]
   */
  const allFast = useCallback(async (voyageId, options = null, legacyArrivalLocation = null) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage || voyage.status !== VOYAGE_STATUS.UNDERWAY) {
      return false;
    }

    // Check if any operations are still in progress
    const activeOps = getActiveOperations(voyageId);
    if (activeOps.length > 0) {
      console.warn('[useVoyages] Cannot All Fast: operations still in progress');
      return { success: false, reason: 'operations_in_progress', operations: activeOps };
    }

    // FT-269: parse options + resolve event time/timezone vs record time.
    const recordedAtDevice = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);
    let eventTime = recordedAtDevice;
    let eventTimezone = getOffsetForTimezone(vesselTz, new Date(recordedAtDevice));
    let delayReason;
    let arrivalLocation = legacyArrivalLocation;
    if (typeof options === 'string') {
      eventTime = options;
      eventTimezone = getOffsetForTimezone(vesselTz, new Date(eventTime));
    } else if (options && typeof options === 'object') {
      if (options.eventTime) eventTime = options.eventTime;
      if (options.eventTimezone) eventTimezone = options.eventTimezone;
      if (options.delayReason) delayReason = options.delayReason;
      if (options.arrivalLocation) arrivalLocation = options.arrivalLocation;
    }
    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const auditContext = networkStatusAtRecord ? { networkStatusAtRecord } : undefined;
    const deltaSeconds = Math.round((Date.now() - new Date(eventTime).getTime()) / 1000);

    const now = eventTime;
    const location = arrivalLocation || voyage.to?.name;

    // Add All Fast log entry — FT-269 carries event-time fields.
    const allFastEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.ALL_FAST,
      timestamp: now,
      eventTime,
      eventTimezone,
      recordedAtDevice,
      recordedAtServer: null,
      ...(delayReason ? { delayReason } : {}),
      ...(auditContext ? { auditContext } : {}),
      data: {
        location,
        position: voyage.to?.position
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    // V2: Add milestone to unified log
    const allFastMilestone = {
      id: generateLogEntryId(),
      type: 'milestone',
      milestoneType: 'all_fast',
      location: location || null,
      timestamp: now,
      eventTime,
      eventTimezone,
      recordedAtDevice,
      recordedAtServer: null,
      ...(delayReason ? { delayReason } : {}),
      ...(auditContext ? { auditContext } : {}),
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            status: VOYAGE_STATUS.ARRIVAL,
            arrivalTime: eventTime,
            to: { ...v.to, name: location },
            logEntries: [...v.logEntries, allFastEntry],
            unifiedLog: [...(v.unifiedLog || []), allFastMilestone],
            // FT-202/196: Set signOff so voyage report shows signed-off status
            // Use confirmedBy/confirmedAt to match ConfirmSignOffModal format exactly
            signOff: {
              confirmedBy: currentUser?.name || 'Unknown',
              role: currentUser?.role || 'Unknown',
              confirmedAt: now
            },
            updatedAt: new Date().toISOString()
          }
        : v
    );

    await setVoyages(updated);

    // FT-269: audit chain entry.
    logChange({
      action: 'voyage_all_fast',
      item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
      reason: delayReason?.code
        ? { code: delayReason.code, label: 'Backdated All Fast', notes: delayReason.note || undefined }
        : { code: 'all_fast', label: 'Voyage All Fast' },
      moduleSpecificData: {
        eventTime,
        eventTimezone,
        recordedAtDevice,
        eventId: allFastMilestone.id,
        voyageNumber: voyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.ARRIVAL,
        to: location || null,
        ...(networkStatusAtRecord ? { networkStatusAtRecord } : {}),
        ...(delayReason ? { delayReason } : {}),
        ...(deltaSeconds > 30 ? { deltaSeconds } : {}),
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    return { success: true };
  }, [can, voyages, setVoyages, currentVessel, currentUser, getActiveOperations, logChange]);

  /**
   * Update pre-arrival checks for a voyage
   * These are optional checks performed before arriving (while still underway)
   */
  const updatePreArrivalChecks = useCallback(async (voyageId, preArrivalData) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage || voyage.status !== VOYAGE_STATUS.UNDERWAY) {
      return false;
    }

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            preArrivalChecks: preArrivalData.checks || [],
            preArrivalTemplateUsed: preArrivalData.template || 'default',
            preArrivalCompletedAt: preArrivalData.completedAt || null,
            updatedAt: new Date().toISOString()
          }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages]);

  /**
   * Update arrival check item
   * @param {string} voyageId - Voyage ID
   * @param {string} checkId - Check ID
   * @param {boolean} completed - Completion status
   * @param {object} extraData - Optional extra data (e.g., { fuelData } for fuel checks)
   */
  const updateArrivalCheck = useCallback(async (voyageId, checkId, completed, extraData = {}) => {
    console.log('[useVoyages] updateArrivalCheck called:', { voyageId, checkId, completed, extraData });

    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    // Use functional update to always get latest voyages state
    let success = false;

    await setVoyages(prevVoyages => {
      const voyage = prevVoyages.find(v => v.id === voyageId);
      if (!voyage || voyage.status !== VOYAGE_STATUS.ARRIVAL) {
        console.warn('[useVoyages] Voyage not found or wrong status:', { voyageId, status: voyage?.status });
        return prevVoyages; // No change
      }

      const updatedChecks = voyage.arrivalChecks.map(check =>
        check.id === checkId
          ? {
              ...check,
              completed,
              completedAt: completed ? new Date().toISOString() : null,
              completedBy: completed ? currentUser?.name || 'Unknown' : null,
              ...extraData  // Spread extra data (e.g., fuelData)
            }
          : check
      );

      success = true;
      console.log('[useVoyages] Updated arrival check:', updatedChecks.find(c => c.id === checkId));

      return prevVoyages.map(v =>
        v.id === voyageId
          ? {
              ...v,
              arrivalChecks: updatedChecks,
              updatedAt: new Date().toISOString()
            }
          : v
      );
    });

    return success;
  }, [can, setVoyages, currentUser]);

  /**
   * Complete voyage - transition to completed status.
   *
   * FT-269: signature extended to accept `{ eventTime, eventTimezone, delayReason }`.
   * Backwards-compat: a missing options arg behaves exactly as before
   * (eventTime = click time).
   *
   * @param {string} voyageId
   * @param {Object|null} [options]
   */
  const completeVoyage = useCallback(async (voyageId, options = null) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage || voyage.status !== VOYAGE_STATUS.ARRIVAL) {
      return false;
    }

    // FT-269: event time vs record time.
    const recordedAtDevice = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);
    let eventTime = recordedAtDevice;
    let eventTimezone = getOffsetForTimezone(vesselTz, new Date(recordedAtDevice));
    let delayReason;
    if (typeof options === 'string') {
      eventTime = options;
      eventTimezone = getOffsetForTimezone(vesselTz, new Date(eventTime));
    } else if (options && typeof options === 'object') {
      if (options.eventTime) eventTime = options.eventTime;
      if (options.eventTimezone) eventTimezone = options.eventTimezone;
      if (options.delayReason) delayReason = options.delayReason;
    }
    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;
    const deltaSeconds = Math.round((Date.now() - new Date(eventTime).getTime()) / 1000);
    const now = eventTime;

    // FT-269: append a "voyage_complete" milestone to unifiedLog so the
    // event has its own record carrying event-time fields + audit context.
    const completeMilestone = {
      id: generateLogEntryId(),
      type: 'milestone',
      milestoneType: 'voyage_complete',
      timestamp: now,
      eventTime,
      eventTimezone,
      recordedAtDevice,
      recordedAtServer: null,
      ...(delayReason ? { delayReason } : {}),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {}),
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            status: VOYAGE_STATUS.COMPLETED,
            completedAt: eventTime,
            unifiedLog: [...(v.unifiedLog || []), completeMilestone],
            updatedAt: recordedAtDevice
          }
        : v
    );

    await setVoyages(updated);

    // FT-269: belt-and-braces audit chain entry.
    logChange({
      action: 'voyage_completed',
      item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
      reason: delayReason?.code
        ? { code: delayReason.code, label: 'Backdated Voyage Complete', notes: delayReason.note || undefined }
        : { code: 'voyage_completed', label: 'Voyage completed' },
      moduleSpecificData: {
        eventTime,
        eventTimezone,
        recordedAtDevice,
        eventId: completeMilestone.id,
        voyageNumber: voyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.COMPLETED,
        ...(networkStatusAtRecord ? { networkStatusAtRecord } : {}),
        ...(delayReason ? { delayReason } : {}),
        ...(deltaSeconds > 30 ? { deltaSeconds } : {}),
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    // Legacy audit trail (separate storage path) — left untouched per brief §17.
    try {
      await logCRUDOperation(
        AUDIT_ACTIONS.UPDATE,
        'voyages',
        { id: voyageId, vesselId: currentVessel?.id, action: 'complete' },
        { userId: currentUser?.id, name: currentUser?.name || 'Unknown' },
        voyage  // beforeState
      );
    } catch (auditError) {
      console.warn('[useVoyages] Audit trail logging failed:', auditError);
    }

    return true;
  }, [can, voyages, setVoyages, currentVessel, currentUser, logChange]);

  /**
   * Cancel voyage with reason. FT-269: now writes an audit chain entry
   * via useAuditTrail so the cancellation appears alongside other voyage
   * state transitions in forensic queries.
   */
  const cancelVoyage = useCallback(async (voyageId, reason) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    // Only allow cancellation of pre_departure voyages
    if (voyage.status !== VOYAGE_STATUS.PRE_DEPARTURE) {
      console.warn('[useVoyages] Can only cancel voyages in pre-departure status');
      return false;
    }

    const now = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            status: VOYAGE_STATUS.CANCELLED,
            cancelReason: reason,
            cancelledAt: now,
            cancelledBy: currentUser?.name || 'Unknown',
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);

    logChange({
      action: 'voyage_cancelled',
      item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
      reason: { code: 'voyage_cancelled', label: 'Voyage cancelled', notes: reason || undefined },
      moduleSpecificData: {
        eventTime: now,
        eventTimezone: getOffsetForTimezone(vesselTz, new Date(now)),
        recordedAtDevice: now,
        voyageNumber: voyage.voyageNumber,
        voyageStatus: VOYAGE_STATUS.CANCELLED,
        priorStatus: voyage.status,
        cancelReason: reason || null,
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));

    return true;
  }, [can, voyages, setVoyages, currentVessel, currentUser, logChange]);

  // ============================================================================
  // LOG ENTRY ACTIONS
  // ============================================================================

  /**
   * Add a log entry to a voyage.
   *
   * FT-269: now accepts an optional `eventOptions` arg carrying
   * `{ eventTime, eventTimezone, delayReason }`. When omitted, behaves
   * identically to the pre-FT-269 implementation.
   */
  const addLogEntry = useCallback(async (voyageId, entryType, data, eventOptions = null) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return null;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return null;

    // Only allow log entries during underway or arrival
    if (voyage.status !== VOYAGE_STATUS.UNDERWAY && voyage.status !== VOYAGE_STATUS.ARRIVAL) {
      console.warn('[useVoyages] Can only add log entries to underway or arrival voyages');
      return null;
    }

    const recordedAtDevice = new Date().toISOString();
    const vesselTz = resolveVesselTimezone(currentVessel);
    let eventTime = recordedAtDevice;
    let eventTimezone = getOffsetForTimezone(vesselTz, new Date(recordedAtDevice));
    let delayReason;
    if (eventOptions && typeof eventOptions === 'object') {
      if (eventOptions.eventTime) eventTime = eventOptions.eventTime;
      if (eventOptions.eventTimezone) eventTimezone = eventOptions.eventTimezone;
      if (eventOptions.delayReason) delayReason = eventOptions.delayReason;
    }
    const networkStatusAtRecord = (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'offline' : null;

    const newEntry = {
      id: generateLogEntryId(),
      type: entryType,
      timestamp: eventTime,
      eventTime,
      eventTimezone,
      recordedAtDevice,
      recordedAtServer: null,
      ...(delayReason ? { delayReason } : {}),
      ...(networkStatusAtRecord ? { auditContext: { networkStatusAtRecord } } : {}),
      data: data || {},
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            logEntries: [...v.logEntries, newEntry],
            updatedAt: new Date().toISOString()
          }
        : v
    );

    await setVoyages(updated);
    return newEntry;
  }, [can, voyages, setVoyages, currentVessel, currentUser]);

  /**
   * FT-269: post-finalisation correction of a past event's eventTime.
   *
   * Updates the source record AND writes a `voyage_event_corrected` audit
   * entry containing the prior values. This satisfies Gemini regulatory
   * review §1: "Any edits to eventTime after initial submission must be
   * recorded as a Correction entry, NOT an overwrite." The source record
   * IS updated for display correctness; the audit chain has the full delta.
   *
   * UI to expose this is deferred to a future ticket (per brief §19);
   * the function is wired so a future ticket can mount a corrections UI
   * without touching the hook again.
   *
   * @param {string} voyageId
   * @param {string} eventId - milestone or log-entry id within the voyage
   * @param {Object} update - { eventTime, eventTimezone, reason }
   */
  const correctVoyageEventTime = useCallback(async (voyageId, eventId, update = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot correct event time');
      return false;
    }
    if (!voyageId || !eventId || !update.eventTime) return false;

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    let priorEvent = null;
    const patchEvent = (e) => {
      if (e?.id !== eventId) return e;
      priorEvent = e;
      return {
        ...e,
        eventTime: update.eventTime,
        eventTimezone: update.eventTimezone || e.eventTimezone,
        timestamp: update.eventTime // keep legacy timestamp aligned
      };
    };

    const updated = voyages.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: (v.unifiedLog || []).map(patchEvent),
        logEntries: (v.logEntries || []).map(patchEvent),
        updatedAt: new Date().toISOString()
      };
    });

    if (!priorEvent) return false;

    // FT-269 §8.9 + §24.11: correctVoyageEventTime is correction-not-overwrite —
    // a new voyage_event_corrected audit entry is appended (NEVER mutating prior
    // entries) and the voyage record's eventTime IS updated. priorEventTime is
    // captured in moduleSpecificData so the auditor can reconstruct the change.
    // If the audit write fails, the voyage record's eventTime is NOT updated
    // (audit write happens first; record update only on audit success).
    //
    // Phase B.1 verification (advisor 2026-06-01): ordering corrected — audit
    // write now precedes setVoyages so a failed audit chain cannot leave a
    // corrected eventTime in the source record without forensic trail.
    // No precedent in the codebase for referencing a prior audit entry's ID;
    // documented as a follow-up if downstream consumers require chain linkage.
    try {
      await logChange({
        action: 'voyage_event_corrected',
        item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
        reason: { code: 'event_time_correction', label: 'Voyage event time correction', notes: update.reason || undefined },
        moduleSpecificData: {
          eventTime: update.eventTime,
          eventTimezone: update.eventTimezone || priorEvent.eventTimezone,
          recordedAtDevice: new Date().toISOString(),
          eventId,
          voyageNumber: voyage.voyageNumber,
          voyageStatus: voyage.status,
          prior: { eventTime: priorEvent.eventTime || priorEvent.timestamp, eventTimezone: priorEvent.eventTimezone || null },
          next: { eventTime: update.eventTime, eventTimezone: update.eventTimezone || priorEvent.eventTimezone || null },
          reason: update.reason || null,
          vesselId: currentVessel?.id || null
        }
      });
    } catch (err) {
      console.warn('[useVoyages] correctVoyageEventTime audit write failed — voyage record NOT updated:', err);
      return false; // abort: do NOT mutate the voyage record without an audit trail
    }

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages, currentVessel, logChange]);

  /**
   * FT-269: reconcile any voyage events that are missing `recordedAtServer`.
   *
   * Primary path is the `sr:voyage:server-stamp` event listener (registered
   * in the useEffect below). This fallback is REQUIRED per brief §24.2
   * for events whose push completed but whose listener never fired
   * (component unmount, app backgrounded, browser refresh during sync).
   *
   * Idempotent: skips the storage write when the fetched value equals
   * the local value (Correction D), so repeated runs cost only GETs.
   * Failure is non-fatal — the next foreground/route change retries.
   */
  const reconcileMissingServerStamps = useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return; // No point asking the server for stamps while offline.
    }
    if (!voyages || voyages.length === 0) return;
    const token = azureApiClient.getToken();
    if (!token) return;
    const apiBase = process.env.REACT_APP_AZURE_API_URL;
    if (!apiBase) return;

    const now = Date.now();
    const sixtySecondsAgo = now - 60_000;

    // Collect candidate (voyageId, eventId) pairs.
    const candidates = [];
    for (const v of voyages) {
      const events = [
        ...(Array.isArray(v.unifiedLog) ? v.unifiedLog : []),
        ...(Array.isArray(v.logEntries) ? v.logEntries : [])
      ];
      for (const e of events) {
        if (!e || !e.id) continue;
        if (e.recordedAtServer) continue; // already stamped
        if (!e.recordedAtDevice) continue; // pre-FT-269 records have no stamp to chase
        const recordedAtMs = new Date(e.recordedAtDevice).getTime();
        if (!Number.isFinite(recordedAtMs)) continue;
        if (recordedAtMs > sixtySecondsAgo) continue; // give the primary path 60s grace
        candidates.push({ voyageId: v.id, eventId: e.id, localValue: e.recordedAtServer || null });
      }
    }
    if (candidates.length === 0) return;

    const merges = [];
    for (const c of candidates) {
      // FT-269 Phase B.1: 5-second AbortController timeout so a hung API
      // doesn't freeze the reconciler (which runs on foreground / route change).
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      try {
        const res = await fetch(`${apiBase}/voyages/${encodeURIComponent(c.voyageId)}/events/${encodeURIComponent(c.eventId)}/server-stamp`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        if (!res.ok) continue;
        const body = await res.json();
        const fetched = body?.recordedAtServer || null;
        if (!fetched) continue;
        if (fetched === c.localValue) continue; // idempotency: no-op (Correction D)
        merges.push({ voyageId: c.voyageId, eventId: c.eventId, recordedAtServer: fetched });
      } catch (err) {
        // Non-fatal: next run will retry.
        if (err && err.name === 'AbortError') {
          console.warn('[useVoyages] reconciler fetch timed out (5s):', c.voyageId, c.eventId);
        } else {
          console.warn('[useVoyages] reconcileMissingServerStamps fetch failed (non-fatal):', err);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    }
    if (merges.length === 0) return;

    // Apply merges in a single setVoyages call to avoid storage churn.
    await setVoyages(prev => prev.map(v => {
      const relevant = merges.filter(m => m.voyageId === v.id);
      if (relevant.length === 0) return v;
      const stampMap = new Map(relevant.map(r => [r.eventId, r.recordedAtServer]));
      const patch = (e) => (e && e.id && stampMap.has(e.id))
        ? { ...e, recordedAtServer: stampMap.get(e.id) }
        : e;
      return {
        ...v,
        unifiedLog: (v.unifiedLog || []).map(patch),
        logEntries: (v.logEntries || []).map(patch)
      };
    }));
  }, [voyages, setVoyages]);

  // FT-269: post-push primary path. The sync bridge dispatches
  // `sr:voyage:server-stamp` with { voyageId, eventId, recordedAtServer }
  // after each successful push. We listen here and merge into the matching
  // event record via the standard useStorageState setter — no bespoke
  // storage path, the change rides the normal sync pipeline.
  //
  // NOTE: as of the FT-269 implementation, the sync bridge does NOT yet
  // emit this event (the brief explicitly forbids modifying push/pull
  // semantics on UniversalSyncBridge.js). The listener is wired up so a
  // future ticket can add the dispatch with no further hook changes.
  // The reconcileMissingServerStamps() fallback (registered in
  // VoyageManagementTab.js) is what actually populates recordedAtServer
  // today.
  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      const { voyageId, eventId, recordedAtServer } = detail;
      if (!voyageId || !eventId || !recordedAtServer) return;
      setVoyages(prev => prev.map(v => {
        if (v.id !== voyageId) return v;
        const patch = (e) => (e?.id === eventId && !e.recordedAtServer)
          ? { ...e, recordedAtServer }
          : e;
        return {
          ...v,
          unifiedLog: (v.unifiedLog || []).map(patch),
          logEntries: (v.logEntries || []).map(patch)
        };
      }));
    };
    window.addEventListener('sr:voyage:server-stamp', handler);
    return () => window.removeEventListener('sr:voyage:server-stamp', handler);
  }, [setVoyages]);

  // ============================================================================
  // OPERATION ACTIONS
  // ============================================================================

  /**
   * Start an inline operation during a voyage
   */
  const startOperation = useCallback(async (voyageId, operationType, operationData = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return null;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage || voyage.status !== VOYAGE_STATUS.UNDERWAY) {
      console.warn('[useVoyages] Can only start operations during underway');
      return null;
    }

    const now = new Date().toISOString();

    const newOperation = {
      id: generateOperationId(),
      type: operationType,
      status: 'in_progress',
      startTime: now,
      endTime: null,
      data: operationData,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    // Add operation start log entry
    const logEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.OPERATION_START,
      timestamp: now,
      data: {
        operationId: newOperation.id,
        operationType,
        ...operationData
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            operations: [...v.operations, newOperation],
            logEntries: [...v.logEntries, logEntry],
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);
    return newOperation;
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * End an inline operation
   */
  const endOperation = useCallback(async (voyageId, operationId, completionData = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const operation = voyage.operations.find(op => op.id === operationId);
    if (!operation || operation.status !== 'in_progress') {
      return false;
    }

    const now = new Date().toISOString();

    // Update operation
    const updatedOperations = voyage.operations.map(op =>
      op.id === operationId
        ? {
            ...op,
            status: 'completed',
            endTime: now,
            data: { ...op.data, ...completionData }
          }
        : op
    );

    // Add operation end log entry
    const logEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.OPERATION_END,
      timestamp: now,
      data: {
        operationId,
        operationType: operation.type,
        duration: new Date(now) - new Date(operation.startTime),
        ...completionData
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            operations: updatedOperations,
            logEntries: [...v.logEntries, logEntry],
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages, currentUser]);

  // ============================================================================
  // PHASE 1 INLINE OPERATIONS
  // ============================================================================

  /**
   * Create a pending operation (before starting)
   * This creates the operation in 'pending' status with pre-op checks
   */
  const createPendingOperation = useCallback(async (voyageId, operationType) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return null;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage || voyage.status !== VOYAGE_STATUS.UNDERWAY) {
      console.warn('[useVoyages] Can only create operations during underway');
      return null;
    }

    // Check for too many active/pending operations
    const existingOps = (voyage.operations || []).filter(
      op => op.status === 'pending' || op.status === 'active'
    );
    if (existingOps.length >= 3) {
      console.warn('[useVoyages] Maximum concurrent operations reached');
      return { error: 'max_operations', message: 'Maximum 3 concurrent operations allowed' };
    }

    const now = new Date().toISOString();
    // Use custom template from voyage settings (falls back to defaults)
    const template = getOperationTemplate(operationType, voyageSettings);

    // Build pre-op checks from config
    const preOpChecks = [];
    if (template?.checks) {
      template.checks.forEach(check => {
        preOpChecks.push({
          id: check.id,
          text: check.text,
          required: check.required || false,
          completed: false,
          completedAt: null,
          completedBy: null,
          skipped: false,
          skippedAt: null,
          skippedBy: null,
          skippedReason: null
        });
      });
    }

    const newOperation = {
      id: generatePendingOperationId(),
      type: operationType,
      status: 'pending',
      createdAt: now,
      startedAt: null,
      endedAt: null,
      preOpChecks,
      operationData: {},
      operationEvents: [],
      cancelledAt: null,
      cancelledBy: null,
      cancelledReason: null,
      cancelledNotes: null,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            operations: [...(v.operations || []), newOperation],
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);
    return newOperation;
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * Update a pre-op check on a pending operation
   */
  const updateOperationPreOpCheck = useCallback(async (
    voyageId,
    operationId,
    checkId,
    { completed = false, skipped = false, skippedReason = null }
  ) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const operation = voyage.operations?.find(op => op.id === operationId);
    if (!operation || operation.status !== 'pending') {
      console.warn('[useVoyages] Can only update checks on pending operations');
      return false;
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown';

    // Check immutability - prevent modifying checks completed more than 5 minutes ago
    const existingCheck = operation.preOpChecks.find(c => c.id === checkId);
    if (existingCheck?.completed && existingCheck.completedAt) {
      const completedTime = new Date(existingCheck.completedAt).getTime();
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      if (completedTime < fiveMinutesAgo) {
        console.warn('[useVoyages] Cannot modify check - audit trail locked after 5 minutes');
        return false;
      }
    }

    const updatedPreOpChecks = operation.preOpChecks.map(check => {
      if (check.id !== checkId) return check;

      if (skipped) {
        return {
          ...check,
          completed: false,
          completedAt: null,
          completedBy: null,
          skipped: true,
          skippedAt: now,
          skippedBy: userName,
          skippedReason: skippedReason || 'Not specified'
        };
      } else {
        return {
          ...check,
          completed,
          completedAt: completed ? now : null,
          completedBy: completed ? userName : null,
          skipped: false,
          skippedAt: null,
          skippedBy: null,
          skippedReason: null
        };
      }
    });

    const updatedOperations = voyage.operations.map(op =>
      op.id === operationId
        ? { ...op, preOpChecks: updatedPreOpChecks }
        : op
    );

    const updated = voyages.map(v =>
      v.id === voyageId
        ? { ...v, operations: updatedOperations, updatedAt: now }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * Update operation data fields (platform, persons, etc.)
   */
  const updateOperationData = useCallback(async (voyageId, operationId, operationData) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const operation = voyage.operations?.find(op => op.id === operationId);
    if (!operation || operation.status !== 'pending') {
      console.warn('[useVoyages] Can only update data on pending operations');
      return false;
    }

    const now = new Date().toISOString();

    const updatedOperations = voyage.operations.map(op =>
      op.id === operationId
        ? { ...op, operationData: { ...op.operationData, ...operationData } }
        : op
    );

    const updated = voyages.map(v =>
      v.id === voyageId
        ? { ...v, operations: updatedOperations, updatedAt: now }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages]);

  /**
   * Start operation - transition from pending to active
   * Validates required checks are completed
   */
  const activateOperation = useCallback(async (voyageId, operationId) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied');
      return { success: false, error: 'permission_denied' };
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return { success: false, error: 'voyage_not_found' };

    const operation = voyage.operations?.find(op => op.id === operationId);
    if (!operation || operation.status !== 'pending') {
      return { success: false, error: 'invalid_operation_status' };
    }

    // Validate all pre-op checks are completed or skipped
    const incompleteChecks = operation.preOpChecks.filter(
      check => !check.completed && !check.skipped
    );
    if (incompleteChecks.length > 0) {
      return {
        success: false,
        error: 'checks_incomplete',
        incompleteChecks: incompleteChecks.map(c => c.id)
      };
    }

    const now = new Date().toISOString();

    // Add operation start log entry
    const logEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.OPERATION_START,
      timestamp: now,
      data: {
        operationId: operation.id,
        operationType: operation.type,
        operationData: operation.operationData,
        completedChecks: operation.preOpChecks
          .filter(c => c.completed)
          .map(c => c.id),
        skippedChecks: operation.preOpChecks
          .filter(c => c.skipped)
          .map(c => ({ id: c.id, reason: c.skippedReason }))
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updatedOperations = voyage.operations.map(op =>
      op.id === operationId
        ? { ...op, status: 'active', startedAt: now }
        : op
    );

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            operations: updatedOperations,
            logEntries: [...(v.logEntries || []), logEntry],
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);
    return { success: true };
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * Cancel an operation (pending or active)
   * Requires reason for audit trail
   */
  const cancelOperation = useCallback(async (
    voyageId,
    operationId,
    reason,
    notes = null
  ) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied');
      return false;
    }

    if (!reason) {
      console.warn('[useVoyages] Cancellation reason required');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const operation = voyage.operations?.find(op => op.id === operationId);
    if (!operation || (operation.status !== 'pending' && operation.status !== 'active')) {
      console.warn('[useVoyages] Can only cancel pending or active operations');
      return false;
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown';

    // Add cancellation log entry if operation was active
    const logEntries = [...(voyage.logEntries || [])];
    if (operation.status === 'active') {
      logEntries.push({
        id: generateLogEntryId(),
        type: LOG_ENTRY_TYPES.OPERATION_END,
        timestamp: now,
        data: {
          operationId: operation.id,
          operationType: operation.type,
          cancelled: true,
          cancelledReason: reason,
          cancelledNotes: notes,
          duration: operation.startedAt
            ? new Date(now) - new Date(operation.startedAt)
            : 0
        },
        createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
      });
    }

    const updatedOperations = voyage.operations.map(op =>
      op.id === operationId
        ? {
            ...op,
            status: 'cancelled',
            endedAt: now,
            cancelledAt: now,
            cancelledBy: userName,
            cancelledReason: reason,
            cancelledNotes: notes
          }
        : op
    );

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            operations: updatedOperations,
            logEntries,
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * Complete an active operation
   */
  const completeOperation = useCallback(async (voyageId, operationId, completionData = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const operation = voyage.operations?.find(op => op.id === operationId);
    if (!operation || operation.status !== 'active') {
      console.warn('[useVoyages] Can only complete active operations');
      return false;
    }

    const now = new Date().toISOString();

    const logEntry = {
      id: generateLogEntryId(),
      type: LOG_ENTRY_TYPES.OPERATION_END,
      timestamp: now,
      data: {
        operationId: operation.id,
        operationType: operation.type,
        operationData: { ...operation.operationData, ...completionData },
        duration: new Date(now) - new Date(operation.startedAt),
        eventCount: operation.operationEvents?.length || 0
      },
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
    };

    const updatedOperations = voyage.operations.map(op =>
      op.id === operationId
        ? {
            ...op,
            status: 'completed',
            endedAt: now,
            operationData: { ...op.operationData, ...completionData }
          }
        : op
    );

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            operations: updatedOperations,
            logEntries: [...(v.logEntries || []), logEntry],
            updatedAt: now
          }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * Get pending operations for a voyage
   */
  const getPendingOperations = useCallback((voyageId) => {
    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return [];
    return (voyage.operations || []).filter(op => op.status === 'pending');
  }, [voyages]);

  /**
   * Get completed/cancelled operations for a voyage
   */
  const getCompletedOperations = useCallback((voyageId) => {
    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return [];
    return (voyage.operations || []).filter(
      op => op.status === 'completed' || op.status === 'cancelled'
    );
  }, [voyages]);

  // ============================================================================
  // VOYAGE NUMBER MANAGEMENT
  // ============================================================================

  /**
   * Edit voyage number (requires reason for audit)
   */
  const editVoyageNumber = useCallback(async (voyageId, newVoyageNumber, reason) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    if (!reason || reason.trim() === '') {
      console.warn('[useVoyages] Reason required for voyage number edit');
      return false;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            voyageNumber: newVoyageNumber,
            voyageNumberEditReason: reason,
            voyageNumberEditedAt: new Date().toISOString(),
            voyageNumberEditedBy: currentUser?.name || 'Unknown',
            updatedAt: new Date().toISOString()
          }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages, currentUser]);

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get voyage statistics
   */
  const getVoyageStats = useCallback(() => {
    const completed = voyages.filter(v => v.status === VOYAGE_STATUS.COMPLETED);
    const thisMonth = completed.filter(v => {
      const date = new Date(v.completedAt);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    });

    // Calculate total voyage time this month
    const totalTimeMs = thisMonth.reduce((sum, v) => {
      if (v.departureTime && v.arrivalTime) {
        return sum + (new Date(v.arrivalTime) - new Date(v.departureTime));
      }
      return sum;
    }, 0);

    const totalHours = Math.round(totalTimeMs / (1000 * 60 * 60) * 10) / 10;

    // Count operations this month
    const operationsThisMonth = thisMonth.reduce((sum, v) => {
      return sum + (v.operations?.length || 0);
    }, 0);

    return {
      totalVoyages: completed.length,
      voyagesThisMonth: thisMonth.length,
      totalHoursThisMonth: totalHours,
      operationsThisMonth,
      activeVoyage: !!activeVoyage
    };
  }, [voyages, activeVoyage]);

  // ============================================================================
  // UNIFIED VOYAGE LOG - INLINE OPERATIONS
  // ============================================================================

  /**
   * Start an inline operation (adds to unified log, no modal)
   */
  const startInlineOperation = useCallback((voyageId, operationType, operationData = {}) => {
    if (!canDo(can, 'voyages', 'write')) {
      console.warn('[useVoyages] startInlineOperation blocked by RBAC');
      return null;
    }

    const operationId = generateOperationId();
    const timestamp = new Date().toISOString();
    const userName = currentUser?.name || currentUser?.email || 'Unknown';

    const newEntry = {
      id: generateLogEntryId(),
      entryType: 'operation_start',
      operationId,
      operationType,
      operationData,
      timestamp,
      createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
    };

    setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: [...(v.unifiedLog || []), newEntry],
        activeOperationId: operationId,
        updatedAt: timestamp
      };
    }));

    return operationId;
  }, [can, currentUser, setVoyages]);

  /**
   * Complete a single check within an active operation
   */
  const completeOperationCheckInline = useCallback((voyageId, operationId, checkId, checkText) => {
    if (!canDo(can, 'voyages', 'write')) {
      console.warn('[useVoyages] completeOperationCheckInline blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();
    const userName = currentUser?.name || currentUser?.email || 'Unknown';

    const newEntry = {
      id: generateLogEntryId(),
      entryType: 'operation_check',
      operationId,
      checkId,
      checkText,
      checkStatus: 'completed',
      timestamp,
      createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
    };

    setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: [...(v.unifiedLog || []), newEntry],
        updatedAt: timestamp
      };
    }));

    return true;
  }, [can, currentUser, setVoyages]);

  /**
   * Skip a check with mandatory reason
   */
  const skipOperationCheckInline = useCallback((voyageId, operationId, checkId, checkText, skipReason, skipNotes = '') => {
    if (!canDo(can, 'voyages', 'write')) {
      console.warn('[useVoyages] skipOperationCheckInline blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();
    const userName = currentUser?.name || currentUser?.email || 'Unknown';

    const newEntry = {
      id: generateLogEntryId(),
      entryType: 'operation_check',
      operationId,
      checkId,
      checkText,
      checkStatus: 'skipped',
      skippedReason: skipReason,
      skippedNotes: skipNotes,
      timestamp,
      createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
    };

    setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: [...(v.unifiedLog || []), newEntry],
        updatedAt: timestamp
      };
    }));

    return true;
  }, [can, currentUser, setVoyages]);

  /**
   * Add a mid-operation entry (position, weather, note during operation)
   */
  const addMidOperationEntry = useCallback((voyageId, operationId, entryType, entryData) => {
    if (!canDo(can, 'voyages', 'write')) {
      console.warn('[useVoyages] addMidOperationEntry blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();
    const userName = currentUser?.name || currentUser?.email || 'Unknown';

    const newEntry = {
      id: generateLogEntryId(),
      entryType: 'operation_event',
      operationId,
      eventType: entryType, // 'position', 'weather', 'note'
      eventData: entryData,
      timestamp,
      createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
    };

    setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: [...(v.unifiedLog || []), newEntry],
        updatedAt: timestamp
      };
    }));

    return true;
  }, [can, currentUser, setVoyages]);

  /**
   * Complete an inline operation (all checks done)
   */
  const completeInlineOperation = useCallback((voyageId, operationId) => {
    if (!canDo(can, 'voyages', 'write')) {
      console.warn('[useVoyages] completeInlineOperation blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();
    const userName = currentUser?.name || currentUser?.email || 'Unknown';

    // Find operation start to calculate duration
    const voyage = voyages.find(v => v.id === voyageId);
    const startEntry = voyage?.unifiedLog?.find(
      e => e.entryType === 'operation_start' && e.operationId === operationId
    );
    const duration = startEntry
      ? new Date(timestamp) - new Date(startEntry.timestamp)
      : 0;

    const newEntry = {
      id: generateLogEntryId(),
      entryType: 'operation_end',
      operationId,
      completionStatus: 'completed',
      duration,
      timestamp,
      createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
    };

    setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: [...(v.unifiedLog || []), newEntry],
        activeOperationId: null,
        updatedAt: timestamp
      };
    }));

    return true;
  }, [can, currentUser, voyages, setVoyages]);

  /**
   * Abort an inline operation with reason
   */
  const abortInlineOperation = useCallback((voyageId, operationId, abortReason, abortNotes = '') => {
    if (!canDo(can, 'voyages', 'write')) {
      console.warn('[useVoyages] abortInlineOperation blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();
    const userName = currentUser?.name || currentUser?.email || 'Unknown';

    // Find operation start to calculate duration
    const voyage = voyages.find(v => v.id === voyageId);
    const startEntry = voyage?.unifiedLog?.find(
      e => e.entryType === 'operation_start' && e.operationId === operationId
    );
    const duration = startEntry
      ? new Date(timestamp) - new Date(startEntry.timestamp)
      : 0;

    const newEntry = {
      id: generateLogEntryId(),
      entryType: 'operation_end',
      operationId,
      completionStatus: 'aborted',
      abortReason,
      abortNotes,
      duration,
      timestamp,
      createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
    };

    setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        unifiedLog: [...(v.unifiedLog || []), newEntry],
        activeOperationId: null,
        updatedAt: timestamp
      };
    }));

    return true;
  }, [can, currentUser, voyages, setVoyages]);

  /**
   * Check if a check can still be modified (within 5 min window)
   */
  const canModifyCheck = useCallback((checkEntry) => {
    if (!checkEntry?.timestamp) return false;
    const checkTime = new Date(checkEntry.timestamp).getTime();
    const now = Date.now();
    return (now - checkTime) < CHECK_IMMUTABILITY_WINDOW;
  }, []);

  // ============================================================================
  // UNIFIED VOYAGE LOG V2 - FLAT LOG STRUCTURE
  // ============================================================================

  /**
   * V2: Add entry to unified log — the central hub for in-voyage events.
   *
   * FT-270: Accepts optional `options` to thread event-time/timezone/audit-context
   * through from the TimePickerModal path. When options are omitted (legacy callers,
   * system-generated entries like GPS auto-fetch on operation start, fuel reading
   * direct writes), the hub preserves the original `new Date().toISOString()` behaviour.
   *
   * FT-270 §17.7 state-machine guard: blocks in-voyage events during DRAFT,
   * PRE_DEPARTURE, COMPLETED, CANCELLED, ABORTED. Returns null with console warn.
   * UI also hides FAB buttons (defence in depth).
   *
   * @param {string} voyageId
   * @param {object} entry - The unified log entry payload (type, plus type-specific fields)
   * @param {object} [options] - FT-270 TimePickerModal options
   * @param {string} [options.eventTime] - ISO 8601 UTC; the user-claimed event time
   * @param {string} [options.eventTimezone] - Numeric offset string e.g. '+0100'
   * @param {string} [options.recordedAtDevice] - ISO 8601 UTC at click time
   * @param {{code?: string, note?: string}} [options.delayReason]
   * @param {{networkStatusAtRecord?: 'offline'}} [options.auditContext]
   * @returns {Promise<object|null>} The new entry, or null when blocked
   */
  const addUnifiedLogEntry = useCallback(async (voyageId, entry, options = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] addUnifiedLogEntry blocked by RBAC');
      return null;
    }

    // FT-270 §17.7: State machine guard — in-voyage events only valid during
    // UNDERWAY or ARRIVAL. Block (and return null) otherwise. This is the safety
    // net behind the UI button hide.
    const voyageForGuard = voyages.find(v => v.id === voyageId);
    if (voyageForGuard) {
      const status = voyageForGuard.status;
      if (status !== VOYAGE_STATUS.UNDERWAY && status !== VOYAGE_STATUS.ARRIVAL) {
        console.warn(`[useVoyages] In-voyage event blocked — voyage not in UNDERWAY or ARRIVAL (status: ${status})`);
        return null;
      }
    }

    // FT-270: prefer caller-supplied eventTime; fall back to click time for legacy callers.
    const clickTime = new Date().toISOString();
    const eventTime = options.eventTime || clickTime;
    const recordedAtDevice = options.recordedAtDevice || clickTime;

    const newEntry = {
      id: generateLogEntryId(),
      timestamp: eventTime, // legacy field == eventTime so existing display layer is unaffected
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      ...entry,
      // FT-270 Phase C.6: ALWAYS set recordedAtDevice + recordedAtServer so
      // reconcileMissingServerStamps (line ~1735) chases every in-voyage event,
      // not just those that came in through the TimePickerModal. The reconciler
      // requires recordedAtDevice to identify candidates but does NOT require
      // eventTime, so this is safe — eventTime/eventTimezone/delayReason still
      // only land when the modal supplied them.
      recordedAtDevice,
      recordedAtServer: null,
      // FT-270: regulatory-grade time pair, only when TimePickerModal supplied them.
      ...(options.eventTime
        ? {
            eventTime,
            eventTimezone: options.eventTimezone,
            ...(options.delayReason ? { delayReason: options.delayReason } : {}),
            ...(options.auditContext ? { auditContext: options.auditContext } : {})
          }
        : {})
    };

    // FT-481: When this entry is operation_complete, simultaneously unlock the
    // matching operation_data entry in the same voyage's unifiedLog.
    // operation_data is created with locked:true (startOperationV2 ~L3219) and
    // was never cleared on completion — causing voyage completion to be permanently
    // blocked for trial users. The unlock happens atomically in the same map pass.
    const completingOperationId = newEntry.type === 'operation_complete' ? newEntry.operationId : null;

    const updated = voyages.map(v =>
      v.id === voyageId
        ? {
            ...v,
            unifiedLog: [
              // FT-481: Clear stale lock on the matching operation_data entry.
              ...(v.unifiedLog || []).map(e =>
                (completingOperationId &&
                  e.type === 'operation_data' &&
                  e.operationId === completingOperationId &&
                  e.locked)
                  ? { ...e, locked: false }
                  : e
              ),
              newEntry
            ],
            // FT-270: updatedAt always reflects record-time, NOT eventTime. Sync
            // ordering depends on monotonically increasing updatedAt per device;
            // using eventTime here would break sync if a user backdated 4h.
            updatedAt: clickTime
          }
        : v
    );

    await setVoyages(updated);
    return newEntry;
  }, [can, voyages, setVoyages, currentUser]);

  /**
   * FT-270: belt-and-braces in-voyage event audit helper. Mirrors the letGo
   * audit pattern at useVoyages.js:1137-1156 verbatim. Fire-and-forget;
   * non-fatal on failure. Used by all 6 in-voyage event wrappers.
   *
   * @param {string} action - One of voyage_position_logged | voyage_weather_logged | voyage_fuel_logged | voyage_note_logged | voyage_operation_started | voyage_operation_completed
   * @param {string} voyageId
   * @param {object} entry - The unifiedLog entry just written (carries eventId + FT-270 fields if present)
   * @param {object} options - The options passed to the hook (TimePickerModal output)
   * @param {object} typeSpecificData - Event-type-specific fields for moduleSpecificData (lat/lon for position, etc.)
   */
  const fireInVoyageAudit = useCallback((action, voyageId, entry, options, typeSpecificData) => {
    const voyage = voyages.find(v => v.id === voyageId);
    // FT-270 Phase C.6: defensive !entry guard. All call sites currently check
    // `if (entry)` before invoking but a missing entry should still no-op here
    // to harden against future refactors.
    if (!voyage || !entry) return;

    const eventTime = entry?.eventTime || options?.eventTime || null;
    const eventTimezone = entry?.eventTimezone || options?.eventTimezone || null;
    const recordedAtDevice = entry?.recordedAtDevice || options?.recordedAtDevice || null;
    const delayReason = options?.delayReason || entry?.delayReason;
    const networkStatusAtRecord = options?.auditContext?.networkStatusAtRecord
      || entry?.auditContext?.networkStatusAtRecord;
    const deltaSeconds = eventTime
      ? Math.round((Date.now() - new Date(eventTime).getTime()) / 1000)
      : 0;

    // FT-270 Phase C.6: truncate delayReason.note to 497 + '…' if > 500 chars,
    // mirroring the logNoteV2 §17.9 pattern. The TimePickerModal enforces
    // maxLength=500 but legacy/programmatic callers could bypass that limit
    // and bloat the audit log.
    const safeNote = (typeof delayReason?.note === 'string' && delayReason.note.length > 500)
      ? `${delayReason.note.slice(0, 497)}…`
      : (delayReason?.note || undefined);

    logChange({
      action,
      item: { id: voyageId, type: 'voyage', name: voyage.voyageNumber },
      reason: delayReason?.code
        ? { code: delayReason.code, label: 'Backdated in-voyage event', notes: safeNote }
        : { code: action, label: action.replace(/_/g, ' ') },
      moduleSpecificData: {
        eventId: entry?.id,
        eventTime,
        eventTimezone,
        recordedAtDevice,
        voyageNumber: voyage.voyageNumber,
        voyageStatus: voyage.status,
        ...(typeSpecificData || {}),
        ...(networkStatusAtRecord ? { networkStatusAtRecord } : {}),
        ...(delayReason ? { delayReason } : {}),
        ...(deltaSeconds > 30 ? { deltaSeconds } : {}),
        vesselId: currentVessel?.id || null
      }
    }).catch(err => console.warn('[useVoyages] audit write failed (non-fatal):', err));
  }, [logChange, voyages, currentVessel]);

  /**
   * V2: Start operation - adds section header, operation data, phase marker, and ALL phase checks
   * Multi-phase operations: All checks from all phases are added at start, but only Phase 1 is active
   * Auto-logs GPS position at operation start if enabled in vessel config
   *
   * FT-270 (§7.1.4 + §17.5): accepts options for TimePickerModal flow. Only the
   * UMBRELLA event — i.e. the section header (entries[0]) and the operation_data
   * entry — carry FT-270 regulatory-grade fields. The GPS auto-fetch entry keeps
   * coords.timestamp + autoLogged:true (system-generated, NOT user-manual; per
   * locked edge-case decision and T16 regression). Phase markers + check entries
   * remain system-generated as before.
   *
   * Audit fires ONCE per operation start (against the section header entry,
   * not per sub-entry). Pre-op checks remain granular per FT-269 lean-storage.
   *
   * Pass 2t: duplicate-section guard at TWO layers — (1) early closure-read
   * fast-path dedup BEFORE GPS fetch (avoids wasted hardware call), (2) re-check
   * INSIDE the setVoyages(prev=>...) functional updater (catches React-batched
   * burst taps where two callers both saw stale closure state). Audit fires
   * only on real start, never on dedup-refuse.
   */
  const startOperationV2 = useCallback(async (voyageId, operationType, operationData = {}, options = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] startOperationV2 blocked by RBAC');
      return null;
    }

    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return null;

    // FT-270 §17.7: State machine guard — only valid during UNDERWAY or ARRIVAL.
    const status = voyage.status;
    if (status !== VOYAGE_STATUS.UNDERWAY && status !== VOYAGE_STATUS.ARRIVAL) {
      console.warn(`[useVoyages] In-voyage event blocked — voyage not in UNDERWAY or ARRIVAL (status: ${status})`);
      return null;
    }

    // Pass 2t — EARLY dedup against closure-read voyage snapshot. Fast-path:
    // if we already have an unmatched section for this planItemId (or for
    // pre_arrival if no planItemId), return its operationId immediately and
    // skip the expensive GPS fetch entirely. The in-updater re-check below
    // is the authoritative defence; this is the performance optimization +
    // first line of defence against same-user re-tap.
    const earlyDedupId = options.skipEarlyDedup ? null : findUnmatchedSectionForStart(voyage, operationType, options.planItemId);
    if (earlyDedupId) {
      console.warn('[useVoyages] startOperationV2 dedup hit (early) — returning existing operationId', { operationId: earlyDedupId, operationType, planItemId: options.planItemId || null });
      return earlyDedupId;
    }

    const operationId = generateOperationId();
    // FT-270: the user-manual umbrella event time. Used for the section header
    // (which represents the operation as a whole) and the operation_data entry.
    // Sub-events (GPS auto-fetch, phase markers, checks) keep system timestamps.
    const clickTime = new Date().toISOString();
    const eventTime = options.eventTime || clickTime;
    const recordedAtDevice = options.recordedAtDevice || clickTime;
    const timestamp = eventTime; // existing variable name; semantics: umbrella event-time

    // FT-270: optional regulatory fields applied ONLY to the umbrella entries
    // (section header + operation_data). Omitted entirely when no TimePicker
    // options were supplied (system-triggered operation starts).
    const umbrellaFt270 = options.eventTime
      ? {
          eventTime,
          eventTimezone: options.eventTimezone,
          recordedAtDevice,
          recordedAtServer: null,
          ...(options.delayReason ? { delayReason: options.delayReason } : {}),
          ...(options.auditContext ? { auditContext: options.auditContext } : {})
        }
      : {};

    // Use custom template from voyage settings (falls back to defaults)
    const template = operationType === 'pre_arrival'
      ? { checks: getPhaseOperationChecks('pre_arrival', voyageSettings), fields: [] }
      : getOperationTemplate(operationType, voyageSettings);

    // Check vessel config for auto-position logging
    const shouldLogPosition = currentVessel?.autoLogPositionOnOperationStart !== false;

    // Build entries to add
    const entries = [];

    // 1. Section header — the UMBRELLA event. Carries FT-270 regulatory fields.
    entries.push({
      id: generateLogEntryId(),
      type: 'section',
      operationId,
      operationType,
      ...(options.planItemId ? { planItemId: options.planItemId } : {}),  // Pass 2o
      timestamp,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      ...umbrellaFt270
    });

    // 2. Auto-capture GPS position if enabled.
    // FT-270 (locked edge-case): the GPS auto-fetched position is SYSTEM-GENERATED.
    // It must NOT carry FT-270 fields and must NOT go through TimePickerModal.
    // The autoLogged:true flag signals "system-generated, not user-manual" — T16
    // regression test depends on this.
    if (shouldLogPosition) {
      try {
        const coords = await getGPSPosition();
        entries.push({
          id: generateLogEntryId(),
          type: 'position',
          operationId,
          lat: coords.lat,
          // FT-270 Phase C.6: GPS hardware (gpsUtils.js) returns `lng`, but the
          // canonical voyage-event field is `lon` (matches logPositionV2 +
          // display/PDF layers). Map here so this auto-logged entry is
          // consistent with manual position entries. Pre-C.6 used `lng` which
          // caused null/undefined coords downstream for every operation start.
          lon: coords.lng,
          accuracy: coords.accuracy,
          timestamp: coords.timestamp, // unchanged: system clock at GPS fix
          note: `${operationType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} start position`,
          createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          autoLogged: true // FT-270 carve-out marker — do NOT remove
        });
      } catch (gpsError) {
        // GPS failed - log a note instead of blocking the operation
        console.warn('[useVoyages] GPS position unavailable at operation start:', gpsError);
        entries.push({
          id: generateLogEntryId(),
          type: 'note',
          operationId,
          text: `GPS position unavailable at operation start: ${gpsError}`,
          timestamp, // system-generated note; no FT-270 fields
          createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          category: 'system'
        });
      }
    }

    // 3. Operation data — the UMBRELLA event detail. Also carries FT-270 fields.
    entries.push({
      id: generateLogEntryId(),
      type: 'operation_data',
      operationId,
      operationType,
      locked: true,
      timestamp,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      ...operationData,
      ...umbrellaFt270
    });

    // 3. Check if multi-phase or legacy checklist
    const isMultiPhase = template?.phases?.length > 0;

    if (isMultiPhase) {
      // Multi-phase: Add phase marker for each phase, then checks for that phase
      template.phases.forEach((phaseConfig) => {
        // Add phase marker (system-generated, no FT-270 fields)
        entries.push({
          id: generateLogEntryId(),
          type: 'phase_marker',
          operationId,
          phase: phaseConfig.phase,
          phaseName: phaseConfig.name,
          phaseStatus: phaseConfig.status,
          timestamp,
          createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
        });

        // Add checks for this phase (system-generated, no FT-270 fields;
        // per-check completion timestamps land via completeCheckV2)
        if (phaseConfig.checks) {
          phaseConfig.checks.forEach(check => {
            entries.push({
              id: generateLogEntryId(),
              type: 'check',
              operationId,
              phase: phaseConfig.phase,
              checkId: check.id,
              text: check.text,
              required: check.required || false,
              completed: false,
              skipped: false,
              timestamp: null,  // No timestamp until completed
              createdAt: timestamp,  // Track when check was added
              createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
            });
          });
        }
      });
    } else {
      // Legacy: flat checks array (backward compatibility)
      if (template?.checks) {
        template.checks.forEach(check => {
          entries.push({
            id: generateLogEntryId(),
            type: 'check',
            operationId,
            checkId: check.id,
            text: check.text,
            required: check.required || false,
            completed: false,
            skipped: false,
            timestamp: null,  // No timestamp until completed
            createdAt: timestamp,  // Track when check was added
            createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }
          });
        });
      }
    }

    // Pass 2t — functional updater. Re-check dedup against AUTHORITATIVE prev
    // state inside the React update transaction. Catches the burst-tap race
    // where two callers both fast-path-passed (early dedup) before either
    // committed their write.
    let dedupHitInsideUpdater = false;
    let committedOperationId = operationId;
    await setVoyages(prev => {
      const prevVoyage = prev.find(v => v.id === voyageId);
      if (prevVoyage) {
        const existingId = findUnmatchedSectionForStart(prevVoyage, operationType, options.planItemId);
        if (existingId) {
          dedupHitInsideUpdater = true;
          committedOperationId = existingId;
          console.warn('[useVoyages] startOperationV2 dedup hit (in-updater) — returning existing operationId', { operationId: existingId, operationType, planItemId: options.planItemId || null });
          return prev;
        }
      }
      return prev.map(v =>
        v.id === voyageId
          ? {
              ...v,
              unifiedLog: [...(v.unifiedLog || []), ...entries],
              // FT-270: updatedAt always reflects record-time (clickTime), NOT eventTime.
              // Sync ordering depends on monotonic updatedAt per device.
              updatedAt: clickTime
            }
          : v
      );
    });

    // Pass 2t — skip audit fire entirely on dedup-refuse. Audit must reflect
    // real state transitions only; a refused-duplicate tap is a no-op.
    if (dedupHitInsideUpdater) {
      return committedOperationId;
    }

    // FT-270 §17.5: audit fires ONCE per operation start, referencing the
    // section header entry (entries[0]) as the umbrella event.
    const sectionHeaderEntry = entries[0];
    fireInVoyageAudit('voyage_operation_started', voyageId, sectionHeaderEntry, options, {
      operationId,
      operationType
    });

    return operationId;
  }, [can, voyages, setVoyages, currentUser, currentVessel, voyageSettings, fireInVoyageAudit]);

  /**
   * V2: Complete a check in the unified log
   */
  const completeCheckV2 = useCallback(async (voyageId, entryId) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] completeCheckV2 blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();

    await setVoyages(prev => {
      const updated = prev.map(v => {
        if (v.id !== voyageId) return v;

        const updatedLog = (v.unifiedLog || []).map(entry => {
          if (entry.id !== entryId || entry.type !== 'check') return entry;
          return {
            ...entry,
            completed: true,
            completedAt: timestamp,
            completedBy: currentUser?.name || 'Unknown'
          };
        });

        return {
          ...v,
          unifiedLog: updatedLog,
          updatedAt: timestamp
        };
      });

      return updated;
    });
    return true;
  }, [can, setVoyages, currentUser]);

  /**
   * V2: Skip a check in the unified log.
   *
   * FT-283 §6.4: also fires a `voyage_check_skipped` audit row with
   * `skipMethod: 'manual'`. Symmetric with `bulkSkipAndComplete`.
   * `useAuditTrail.logChange` accepts arbitrary action strings — no
   * registration needed.
   */
  const skipCheckV2 = useCallback(async (voyageId, entryId, skipReason, skipNotes = '') => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] skipCheckV2 blocked by RBAC');
      return false;
    }

    const timestamp = new Date().toISOString();

    const updated = voyages.map(v => {
      if (v.id !== voyageId) return v;

      const updatedLog = (v.unifiedLog || []).map(entry => {
        if (entry.id !== entryId || entry.type !== 'check') return entry;
        return {
          ...entry,
          skipped: true,
          skipReason,
          skipNotes,
          skippedAt: timestamp,
          skippedBy: currentUser?.name || 'Unknown'
        };
      });

      return {
        ...v,
        unifiedLog: updatedLog,
        updatedAt: timestamp
      };
    });

    await setVoyages(updated);

    // FT-283 §6.4: per-check audit. Fire-and-forget (fireInVoyageAudit handles errors).
    fireInVoyageAudit('voyage_check_skipped', voyageId, {
      id: entryId,
      skippedAt: timestamp,
      eventTime: timestamp
    }, {}, {
      checkEntryId: entryId,
      skipReason,
      skipMethod: 'manual'
    });

    return true;
  }, [can, voyages, setVoyages, currentUser, fireInVoyageAudit]);


  /**
   * V2: Complete an operation (add operation_complete entry).
   * FT-270: accepts options for TimePickerModal flow + fires audit.
   *
   * FT-283 §9.3: accepts `options.extraAuditData` with a whitelist
   * (`bulkSkipReason`, `skippedCheckIds`, `clockSkew`). Whitelisted keys are
   * spread into the audit row's `moduleSpecificData`. Unknown keys are
   * console.warn-dropped to prevent caller mistakes from clobbering audit
   * user identity. The `extraAuditData` envelope itself is stripped from the
   * options passed to `addUnifiedLogEntry` so it never lands on the
   * persistent unifiedLog entry.
   */
  const completeOperationV2 = useCallback(async (voyageId, operationId, options = {}) => {
    const ALLOWED_EXTRA = ['bulkSkipReason', 'skippedCheckIds', 'clockSkew'];
    // Sub-ticket C: pluck planItemId off the options envelope. planItemId
    // rides as a TOP-LEVEL field on the persistent unifiedLog entry (NOT under
    // extraAuditData) so the §3.5 observer can filter the hot unifiedLog by
    // e.planItemId === planItem.id without descending into moduleSpecificData.
    // It is ALSO carried in moduleSpecificData for the audit row (D inherits).
    const { extraAuditData, planItemId, ...entryOptions } = options;
    const safeExtra = Object.fromEntries(
      Object.entries(extraAuditData || {}).filter(([k]) => {
        const ok = ALLOWED_EXTRA.includes(k);
        if (!ok) console.warn(`[useVoyages] completeOperationV2 dropped unknown extraAuditData key: ${k}`);
        return ok;
      })
    );
    const entry = await addUnifiedLogEntry(voyageId, {
      type: 'operation_complete',
      operationId,
      ...(planItemId ? { planItemId } : {})  // Sub-ticket C
    }, entryOptions);
    if (entry) {
      fireInVoyageAudit('voyage_operation_completed', voyageId, entry, entryOptions, {
        operationId,
        ...(planItemId ? { planItemId } : {}),  // Sub-ticket C
        ...safeExtra
      });
    }
    return entry;
  }, [addUnifiedLogEntry, fireInVoyageAudit]);

  /**
   * FT-283 §6.2: atomic bulk skip + operation_complete.
   *
   * Single `setVoyages(prev => ...)` updater performs ALL skip mutations and
   * appends the `operation_complete` entry in one go. Eliminates the v3
   * closure race where sequential `skipChecksV2` + `completeOperationV2` calls
   * could land the operation_complete on a stale voyages snapshot (skipped
   * checks lost on reload/sync replay).
   *
   * Audit fan-out runs AFTER state lands. `fireInVoyageAudit` is
   * fire-and-forget — no awaits.
   *
   * @param {string} voyageId
   * @param {Array<{checkEntryId: string, reason: string, notes?: string}>} skipItems
   * @param {Object} completeOptions
   *   - eventTime: ISO 8601 UTC (user-claimed event time; defaults to clickTime)
   *   - eventTimezone: numeric offset string e.g. '+0100'
   *   - recordedAtDevice: ISO 8601 UTC at click time
   *   - operationId: the operation being completed
   *   - bulkSkipReason: the bulk reason text (audit-only)
   *   - skippedCheckIds: array of check entry IDs (audit-only)
   *   - clockSkew: boolean (audit-only, marks pre-arrival clamp event)
   *   - delayReason: { code, note? } from the inline time field (persistent)
   *   - auditContext: { networkStatusAtRecord? } (persistent + audit)
   */
  const bulkSkipAndComplete = useCallback(async (voyageId, skipItems, completeOptions = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] bulkSkipAndComplete blocked by RBAC');
      return null;
    }

    const voyageForGuard = voyages.find(v => v.id === voyageId);
    if (!voyageForGuard) return null;
    if (voyageForGuard.status !== VOYAGE_STATUS.UNDERWAY
        && voyageForGuard.status !== VOYAGE_STATUS.ARRIVAL) {
      console.warn(`[useVoyages] bulkSkipAndComplete blocked — voyage not UNDERWAY/ARRIVAL (status: ${voyageForGuard.status})`);
      return null;
    }

    const clickTime = new Date().toISOString();
    const eventTime = completeOptions.eventTime || clickTime;
    const recordedAtDevice = completeOptions.recordedAtDevice || clickTime;
    const skipMap = new Map(skipItems.map(i => [i.checkEntryId, i]));

    const ALLOWED_EXTRA = ['bulkSkipReason', 'skippedCheckIds', 'clockSkew'];
    const extra = Object.fromEntries(
      Object.entries(completeOptions).filter(([k]) => ALLOWED_EXTRA.includes(k))
    );
    // Sub-ticket C: pluck planItemId — bulkSkipAndComplete builds the
    // completeEntry INLINE (does NOT call addUnifiedLogEntry), so plumbing
    // mirrors §4.1 here as well as on the audit fan-out below.
    const planItemId = completeOptions.planItemId || null;

    const completeEntry = {
      id: generateLogEntryId(),
      type: 'operation_complete',
      operationId: completeOptions.operationId,
      ...(planItemId ? { planItemId } : {}),  // Sub-ticket C
      timestamp: eventTime,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      recordedAtDevice,
      recordedAtServer: null,
      ...(completeOptions.eventTime ? {
        eventTime,
        eventTimezone: completeOptions.eventTimezone,
        ...(completeOptions.delayReason ? { delayReason: completeOptions.delayReason } : {}),
        ...(completeOptions.auditContext ? { auditContext: completeOptions.auditContext } : {})
      } : {})
    };

    // ATOMIC: single setVoyages updater commits skip mutations + operation_complete.
    // FT-481: Also unlocks the matching operation_data entry in the same pass so
    // the stale locked:true does not permanently block voyage completion.
    const completingOpId = completeOptions.operationId;
    await setVoyages(prev => prev.map(v => {
      if (v.id !== voyageId) return v;
      const skippedAt = clickTime;
      const updatedLog = (v.unifiedLog || []).map(entry => {
        // FT-481: Unlock operation_data for this operation.
        if (completingOpId &&
            entry.type === 'operation_data' &&
            entry.operationId === completingOpId &&
            entry.locked) {
          return { ...entry, locked: false };
        }
        const item = skipMap.get(entry.id);
        if (!item || entry.type !== 'check') return entry;
        return {
          ...entry,
          skipped: true,
          skipReason: item.reason,
          skipNotes: item.notes || '',
          skippedAt,
          skippedBy: currentUser?.name || 'Unknown',
          skipMethod: 'bulk'
        };
      });
      return {
        ...v,
        unifiedLog: [...updatedLog, completeEntry],
        updatedAt: clickTime
      };
    }));

    // Audit fan-out (fire-and-forget; no awaits).
    for (const item of skipItems) {
      fireInVoyageAudit('voyage_check_skipped', voyageId, {
        id: item.checkEntryId,
        skippedAt: clickTime,
        eventTime: clickTime
      }, {}, {
        checkEntryId: item.checkEntryId,
        skipReason: item.reason,
        skipMethod: 'bulk'
      });
    }
    fireInVoyageAudit('voyage_operation_completed', voyageId, completeEntry, completeOptions, {
      operationId: completeOptions.operationId,
      ...(planItemId ? { planItemId } : {}),  // Sub-ticket C
      ...extra
    });

    return completeEntry;
  }, [can, voyages, setVoyages, currentUser, fireInVoyageAudit]);

  /**
   * V2: Log position.
   * FT-270: signature extended to accept source + manualReason (closing the
   * pre-FT-270 silent-drop bug — UnderwayPhase.js was already passing 6 args
   * but the function only accepted 4). Also accepts options for TimePickerModal
   * flow + fires audit.
   */
  const logPositionV2 = useCallback(async (voyageId, lat, lon, accuracy = null, source = null, manualReason = null, options = {}) => {
    // FT-270 Phase C.6: defensive validation. Reject NaN/Infinity/strings and
    // out-of-range coords before they reach unifiedLog or audit. Prevents
    // corrupted log entries + downstream crashes in display/PDF layers.
    if (typeof lat !== 'number' || typeof lon !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      console.warn('[useVoyages] logPositionV2 rejected invalid lat/lon:', { lat, lon });
      return null;
    }
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      console.warn('[useVoyages] logPositionV2 rejected out-of-range lat/lon:', { lat, lon });
      return null;
    }
    // Sub-ticket C: pluck planItemId so it lands on the persistent entry
    // top-level AND in the audit moduleSpecificData. The remaining entryOptions
    // (eventTime / eventTimezone / delayReason / auditContext / recordedAtDevice)
    // flow into addUnifiedLogEntry unchanged.
    const { planItemId, ...entryOptions } = options || {};
    const entry = await addUnifiedLogEntry(voyageId, {
      type: 'position',
      lat,
      lon,
      accuracy,
      ...(source ? { source } : {}),
      ...(manualReason ? { manualReason } : {}),
      ...(planItemId ? { planItemId } : {})  // Sub-ticket C
    }, entryOptions);
    if (entry) {
      fireInVoyageAudit('voyage_position_logged', voyageId, entry, entryOptions, {
        lat,
        lon,
        accuracy,
        ...(source ? { source } : {}),
        ...(manualReason ? { manualReason } : {}),
        ...(planItemId ? { planItemId } : {})  // Sub-ticket C
      });
    }
    return entry;
  }, [addUnifiedLogEntry, fireInVoyageAudit]);

  /**
   * V2: Log weather.
   * FT-270: accepts options for TimePickerModal flow + fires audit.
   * moduleSpecificData flattens weatherData fields at the top level (per §17.9).
   */
  const logWeatherV2 = useCallback(async (voyageId, weatherData, options = {}) => {
    const entry = await addUnifiedLogEntry(voyageId, {
      type: 'weather',
      ...weatherData
    }, options);
    if (entry) {
      // FT-270 §17.9: flatten weather fields at top of moduleSpecificData.
      fireInVoyageAudit('voyage_weather_logged', voyageId, entry, options, {
        ...(weatherData || {})
      });
    }
    return entry;
  }, [addUnifiedLogEntry, fireInVoyageAudit]);

  /**
   * V2: Log note (with optional linked entry for annotations).
   * FT-270: accepts options for TimePickerModal flow + fires audit.
   * moduleSpecificData truncates text to 497 + '…' if > 500 chars (per §17.9).
   */
  const logNoteV2 = useCallback(async (voyageId, text, linkedEntryId = null, options = {}) => {
    const entry = await addUnifiedLogEntry(voyageId, {
      type: 'note',
      text,
      linkedEntryId  // If set, this note is an annotation on another entry
    }, options);
    if (entry) {
      // FT-270 §17.9: truncate audit-log text to 497 chars + '…' if >500 chars.
      const safeText = (typeof text === 'string' && text.length > 500)
        ? `${text.slice(0, 497)}…`
        : (text || '');
      fireInVoyageAudit('voyage_note_logged', voyageId, entry, options, {
        text: safeText,
        ...(linkedEntryId ? { linkedEntryId } : {})
      });
    }
    return entry;
  }, [addUnifiedLogEntry, fireInVoyageAudit]);

  /**
   * V2: Log comms.
   *
   * FT-270: NOT in scope for this ticket. Signature unchanged. Continues to
   * delegate to addUnifiedLogEntry with no options (legacy path).
   */
  const logCommsV2 = useCallback(async (voyageId, station, message) => {
    return addUnifiedLogEntry(voyageId, {
      type: 'comms',
      station,
      message
    });
  }, [addUnifiedLogEntry]);

  /**
   * V2: Add milestone (LET GO or ALL FAST).
   *
   * FT-270: NOT in scope for this ticket. Signature unchanged. Continues to
   * delegate to addUnifiedLogEntry with no options (legacy path).
   */
  const addMilestoneV2 = useCallback(async (voyageId, milestoneType, location = null) => {
    return addUnifiedLogEntry(voyageId, {
      type: 'milestone',
      milestoneType,
      location
    });
  }, [addUnifiedLogEntry]);

  // ============================================================================
  // FUEL TRACKING FUNCTIONS
  // ============================================================================

  /**
   * Set departure fuel level (called during LET GO)
   * @param {string} voyageId - ID of the voyage
   * @param {number} fuelLevel - Fuel level percentage (0-100)
   */
  const setDepartureFuel = useCallback(async (voyageId, fuelLevel) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] setDepartureFuel blocked by RBAC');
      return false;
    }

    // Use functional update to avoid stale closure race conditions
    setVoyages(prevVoyages => {
      return prevVoyages.map(v => {
        if (v.id !== voyageId) return v;
        return {
          ...v,
          departureFuel: fuelLevel,
          currentFuel: fuelLevel, // Initially same as departure
          updatedAt: new Date().toISOString()
        };
      });
    });

    return true;
  }, [can, setVoyages]);

  /**
   * Set arrival fuel level (called during ALL FAST)
   * @param {string} voyageId - ID of the voyage
   * @param {number} fuelLevel - Fuel level percentage (0-100)
   */
  const setArrivalFuel = useCallback(async (voyageId, fuelLevel) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] setArrivalFuel blocked by RBAC');
      return false;
    }

    // Use functional update to avoid stale closure race conditions
    setVoyages(prevVoyages => {
      return prevVoyages.map(v => {
        if (v.id !== voyageId) return v;
        return {
          ...v,
          arrivalFuel: fuelLevel,
          currentFuel: fuelLevel,
          updatedAt: new Date().toISOString()
        };
      });
    });

    return true;
  }, [can, setVoyages]);

  /**
   * Log a fuel reading to the unified log (mid-voyage).
   *
   * FT-270: extended to accept options (TimePickerModal flow) + fires audit.
   *
   * Design note (§7.1.3): logFuelReading does NOT delegate to
   * addUnifiedLogEntry because it ALSO mutates voyage.currentFuel atomically.
   * Refactoring to use the hub would require either a 2nd setVoyages pass
   * (breaking atomicity) or extending the hub to accept arbitrary voyage-record
   * patches (cross-caller risk). The independent extension below inlines the
   * FT-270 options handling (mirrors §5.1 hub shape) and preserves the
   * currentFuel side-effect verbatim.
   *
   * @param {string} voyageId - ID of the voyage
   * @param {number} fuelLevel - Fuel level percentage (0-100)
   * @param {string} notes - Optional notes
   * @param {object} [options] - FT-270 TimePickerModal options (see addUnifiedLogEntry)
   */
  const logFuelReading = useCallback(async (voyageId, fuelLevel, notes = '', options = {}) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] logFuelReading blocked by RBAC');
      return null;
    }

    // FT-270 §17.7: State machine guard — only valid during UNDERWAY or ARRIVAL.
    const voyageForGuard = voyages.find(v => v.id === voyageId);
    if (voyageForGuard) {
      const status = voyageForGuard.status;
      if (status !== VOYAGE_STATUS.UNDERWAY && status !== VOYAGE_STATUS.ARRIVAL) {
        console.warn(`[useVoyages] In-voyage event blocked — voyage not in UNDERWAY or ARRIVAL (status: ${status})`);
        return null;
      }
    }

    const clickTime = new Date().toISOString();
    const eventTime = options.eventTime || clickTime;
    const recordedAtDevice = options.recordedAtDevice || clickTime;
    const entryId = `fuel_${Date.now()}`;

    const entry = {
      type: 'fuel_reading',
      id: entryId,
      fuelLevel,
      notes,
      timestamp: eventTime,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      ...(options.eventTime
        ? {
            eventTime,
            eventTimezone: options.eventTimezone,
            recordedAtDevice,
            recordedAtServer: null,
            ...(options.delayReason ? { delayReason: options.delayReason } : {}),
            ...(options.auditContext ? { auditContext: options.auditContext } : {})
          }
        : {})
    };

    const updated = voyages.map(v => {
      if (v.id !== voyageId) return v;
      return {
        ...v,
        currentFuel: fuelLevel, // preserved side-effect (T26 regression-checked)
        unifiedLog: [...(v.unifiedLog || []), entry],
        // FT-270: updatedAt always reflects record-time.
        updatedAt: clickTime
      };
    });

    await setVoyages(updated);

    // FT-270 Phase C.6: truncate fuel notes for consistency with logNoteV2 §17.9.
    const safeFuelNotes = (typeof notes === 'string' && notes.length > 500)
      ? `${notes.slice(0, 497)}…`
      : (notes || '');

    // FT-270: belt-and-braces audit.
    fireInVoyageAudit('voyage_fuel_logged', voyageId, entry, options, {
      fuelLevel,
      notes: safeFuelNotes
    });

    return entryId;
  }, [can, voyages, setVoyages, currentUser, fireInVoyageAudit]);

  // ============================================================================
  // FT-201: MASTER DESIGNATION
  // ============================================================================

  /**
   * Set the designated Master for a voyage
   * FT-201: Creates an immutable audit snapshot at the moment of selection
   * @param {string} voyageId - ID of the voyage
   * @param {string} masterId - ID of the crew member designated as Master
   * @param {Object} masterDesignation - Audit snapshot from createMasterDesignationSnapshot()
   */
  const setMasterDesignation = useCallback(async (voyageId, masterId, masterDesignation) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] setMasterDesignation blocked by RBAC');
      return false;
    }

    if (!masterId || !masterDesignation) {
      console.warn('[useVoyages] setMasterDesignation: missing masterId or masterDesignation');
      return false;
    }

    // Use functional update to avoid stale closure race conditions
    setVoyages(prevVoyages => {
      return prevVoyages.map(v => {
        if (v.id !== voyageId) return v;
        return {
          ...v,
          masterId,
          masterDesignation, // Immutable audit snapshot
          updatedAt: new Date().toISOString()
        };
      });
    });

    console.log('[useVoyages] Master designated:', { voyageId, masterId, masterName: masterDesignation?.masterName });
    return true;
  }, [can, setVoyages]);

  // ============================================================================
  // FT-196: UPDATE VOYAGE (for sign-off and other updates)
  // ============================================================================

  /**
   * Update a voyage record (used for sign-off, etc.)
   * @param {Object} updatedVoyage - The full updated voyage object
   */
  const updateVoyage = useCallback(async (updatedVoyage) => {
    if (!canDo(can, 'voyages', 'update')) {
      console.warn('[useVoyages] Permission denied: cannot update voyage');
      return false;
    }

    if (!updatedVoyage?.id) {
      console.warn('[useVoyages] updateVoyage: missing voyage id');
      return false;
    }

    const updated = voyages.map(v =>
      v.id === updatedVoyage.id
        ? { ...v, ...updatedVoyage, updatedAt: new Date().toISOString() }
        : v
    );

    await setVoyages(updated);
    return true;
  }, [can, voyages, setVoyages]);

  // ============================================================================
  // Sub-ticket C: per-plan-item state mutations. Sibling hook in its own
  // file (synthesis A4 TDZ defence). Composed here so the consumer accesses
  // updatePlanItemState / skipPlanItemState off the useVoyages surface in
  // the SAME pattern as completeCheckV2 / logPositionV2 etc.
  // See CODING-BRIEF-C.md §3.4 + §4.5.
  // ============================================================================
  const {
    updatePlanItem: updatePlanItemState,
    skipPlanItem: skipPlanItemState
  } = useVoyagePlanItemState({ voyages, setVoyages, currentUser, can });

  // ============================================================================
  // FT-489: Discard an orphaned pre_arrival operation from the voyage log.
  // Removes ALL unifiedLog entries for the given operationId (section +
  // any associated operation_data and check entries). Called from UnderwayPhase
  // when the user chooses "Discard and start again" in the orphan-recovery prompt.
  // ============================================================================
  const discardOrphanedOperation = useCallback(async (voyageId, orphanedOperationId) => {
    await setVoyages(prev => prev.map(v =>
      v.id === voyageId
        ? {
            ...v,
            unifiedLog: (v.unifiedLog || []).filter(
              e => !e || e.operationId !== orphanedOperationId
            ),
            updatedAt: new Date().toISOString(),
          }
        : v
    ));
  }, [setVoyages]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Data
    voyages,
    activeVoyage,
    draftVoyage, // FT-269

    // Voyage lifecycle
    startVoyage,
    startVoyageAndDepart,
    letGo,
    allFast,
    completeVoyage,
    cancelVoyage,

    // FT-269: draft progressive-save lifecycle
    createDraftVoyage,
    updateDraftVoyage,
    abortDraftVoyage,
    correctVoyageEventTime,
    reconcileMissingServerStamps,

    // Pre-departure checks
    updatePreDepartureCheck,
    canLetGo,

    // Pre-arrival checks (optional, before arriving)
    updatePreArrivalChecks,

    // Arrival checks (post-arrival reminders)
    updateArrivalCheck,

    // Log entries
    addLogEntry,

    // Operations (legacy)
    startOperation,
    endOperation,
    getActiveOperations,

    // Inline Operations (Phase 1 - card based)
    createPendingOperation,
    updateOperationPreOpCheck,
    updateOperationData,
    activateOperation,
    completeOperation,
    cancelOperation,
    getPendingOperations,
    getCompletedOperations,

    // Unified Voyage Log (Phase 2 - inline flow)
    startInlineOperation,
    completeOperationCheckInline,
    skipOperationCheckInline,
    addMidOperationEntry,
    completeInlineOperation,
    abortInlineOperation,
    canModifyCheck,

    // Voyage number
    editVoyageNumber,
    generateVoyageNumber: () => generateVoyageNumber(voyages),

    // Queries
    getVoyage,
    getRecentVoyages,
    getVoyageStats,

    // V2 Unified Log Functions
    addUnifiedLogEntry,
    startOperationV2,
    completeCheckV2,
    skipCheckV2,
    completeOperationV2,
    bulkSkipAndComplete,  // FT-283: atomic bulk skip + complete
    logPositionV2,
    logWeatherV2,
    logNoteV2,
    logCommsV2,
    addMilestoneV2,

    // Fuel Tracking
    setDepartureFuel,
    setArrivalFuel,
    logFuelReading,

    // FT-196: Generic voyage update
    updateVoyage,

    // FT-201: Master Designation
    setMasterDesignation,

    // Sub-ticket C: per-voyage-plan-item state mutations (see §4.5).
    updatePlanItemState,
    skipPlanItemState,

    // FT-489: Orphan recovery — remove a stale pre_arrival operation from the log.
    discardOrphanedOperation,
  };
};

export default useVoyages;
